import { useCallback, useEffect, useMemo, useRef, useState } from 'preact/hooks';
import {
  ArrowLeft,
  Bug,
  CircleHelp,
  Compass,
  Film,
  FolderOpen,
  Link2,
  MoreHorizontal,
  PencilLine,
  Save,
  Sparkles,
  Volume2,
  VolumeX,
} from 'lucide-preact';
import { createCoasterRuntime } from '../app/runtime.ts';
import type {
  CoasterRuntime,
  HeroCaptureBundle,
  Phase9ReviewCameraId,
  Phase9ReviewPaletteId,
  PublishingDetails,
  RuntimeSnapshot,
} from '../app/runtime-types.ts';
import {
  createPhase4PlatformAdapter,
  isLocalPhase4QaPlatformAdapter,
  type CloudDraftRecord,
  type CloudDraftSummary,
  type CoasterViewer,
  type DiscoveryCatalogPage,
  type MyCoasterSummary,
  type PublicCatalogSort,
  type PublicCoasterCard,
  type PublishStatusRecord,
  type PublicCoasterRecord,
  type ReadOnlyShare,
  type ReadOnlyShareRecord,
  type BunchaWorldLaunchContext,
  WorldLaunchSession,
  buildEditorResetPath,
  buildPublicCoasterUrl,
  buildReadOnlyShareUrl,
  matchesWorldLaunch,
  mergeDiscoveryCatalog,
  ownerProjectStatusLabel,
  readLocalQaUrlContext,
  resolvePublicBaseUrl,
} from '../platform/index.ts';
import {
  createNamedProjectRepository,
  createDraftPreview,
  DraftPreviewRepository,
  localQaDraftPersistenceScope,
  type DraftPersistenceScope,
} from '../persistence/index.ts';
import {
  CreatorPublishWorkspaceRepository,
  createCloudSyncFingerprint,
  createEmptyPublishWorkspace,
  createHeroCameraFingerprint,
  createVisualProjectFingerprint,
  catalogHoverArtifactMatches,
  type CatalogHoverCaptureState,
  type CatalogHoverVideoArtifact,
  type CreatorPublishWorkspaceV1,
  type PublishConflictState,
  type PublishHydrationState,
} from '../publishing/index.ts';
import type { CoasterProject } from '../schema/index.ts';
import { EditorShell } from './EditorShell.tsx';
import { PublishPanel, type PublishPanelStatus } from './PublishPanel.tsx';
import { VisitorLaunchPanel } from './VisitorLaunchPanel.tsx';
import { MediaStudio } from './MediaStudio.tsx';
import { ShareDraftDialog } from './ShareDraftDialog.tsx';
import {
  BrowsePanel,
  MyCoastersPanel,
  type BrowseCollection,
  type HubView,
} from './DiscoveryHub.tsx';
import type * as ReactionCamUi from './ReactionCam.tsx';
import { Button, CloseButton, Dialog, IconButton, StatusPill } from './primitives.tsx';
import { UI_COPY } from './ui-copy.ts';
import { BrandLogo } from './BrandLogo.tsx';
import { LoadingMark } from './LoadingMark.tsx';
import { HelpCenter } from './HelpCenter.tsx';
import { OnboardingCoachmark } from './OnboardingCoachmark.tsx';
import {
  createLocalSandboxMonetizationAdapter,
  createUnavailableMonetizationAdapter,
  type LocalSandboxScenario,
} from '../monetization/index.ts';

const POST_TEST_ACTIONS_REASON =
  'Complete a Test Ride to unlock Recording, Sharing, and Publishing.';

type ExitDestination = 'main_menu' | 'creator_guide' | 'finish_challenge';

const INITIAL_SNAPSHOT: RuntimeSnapshot = {
  mode: 'menu',
  ready: false,
  editorRevision: 0,
  status: 'Loading coaster…',
  selectedNodeId: null,
  selectedNodeIds: [],
  selectedNodeLabel: null,
  selectedNodeIndex: null,
  nodeIds: [],
  nodeCount: 0,
  selectedHeightM: null,
  selectedPosition: null,
  selectedBankDegrees: null,
  selectedRollWindingDegrees: null,
  selectedRollWindingMixed: false,
  selectedSectionId: null,
  selectedSectionIndex: null,
  selectedSectionLabel: null,
  selectedSectionBehavior: null,
  selectedSectionAllowedBehaviors: [],
  selectedAuthoredPieceId: null,
  selectedAuthoredPieceIsActiveEndpoint: true,
  canDeleteSelectedPiece: false,
  selectedPieceDeleteBlockedReason: null,
  authoredGapCount: 0,
  authoredEndpointSide: 'end',
  authoredEndpointLabel: null,
  canSwitchAuthoredEndpoint: false,
  autoBankPending: false,
  sectionKinds: [],
  canUndo: false,
  canRedo: false,
  canDeleteLastPiece: false,
  finishChecking: false,
  stationPlacement: 'near',
  stationPlacementMode: {
    active: false,
    position: { x: -32, y: 4, z: 16 },
    headingStep: 0,
    valid: true,
    reason: null,
  },
  fixtureId: 'lift-drop',
  fixtureTitle: 'Lift & Drop Lab',
  activeTool: 'build',
  editorMode: 'basic',
  advancedLocked: false,
  modularBuild: false,
  authoredGridBuild: false,
  authoredCatalog: [],
  authoredSelection: { descriptorId: 'straight.1t', trackType: 'normal' },
  buildOptions: {
    length: 'medium',
    turnDegrees: 45,
    radius: 'medium',
    pitchTargetDegrees: 30,
    bankTargetDegrees: -30,
  },
  buildSelection: {
    shapeId: 'straight',
    parameters: { length: 'medium' },
    trackType: 'normal',
  },
  buildShapeChoices: [],
  buildTypeChoices: [],
  routeGuideEnabled: true,
  routeGuideStatus: 'idle',
  routeGuideSuggestions: [],
  routeGuideMessage: '',
  routeGuidePlan: null,
  selectionTarget: 'piece',
  snappingEnabled: true,
  activeOverlay: 'none',
  saveState: 'clean',
  hasSavedDraft: false,
  hasUnsavedChanges: false,
  topologyStatus: 'closed',
  constructionState: 'closed_ready',
  buildEnd: null,
  plotBounds: { minX: -70, maxX: 70, minZ: -50, maxZ: 50, minY: 1, maxY: 65 },
  testAllowed: false,
  testDisabledReason: 'Checking your ride.',
  recording: {
    allowed: false,
    certified: false,
    source: null,
    disabledReason: 'Complete successful Test Ride to unlock Recording.',
  },
  validation: {
    revision: 0,
    issues: [],
    samples: [],
    blockerCount: 0,
    warningCount: 0,
    testAllowed: false,
    summary: 'Checking track',
  },
  validationPending: true,
  preview: null,
  appearance: {
    themeId: 'park_model',
    trackColor: '#2457b2',
    supportColor: '#343a40',
    trainColor: '#d94a38',
  },
  supportsVisible: true,
  sceneryVisible: true,
  sceneryCount: 0,
  sceneryPlacement: { active: false, assetId: null, rotationDegrees: 0 },
  plotSizeMeters: { x: 160, z: 120 },
  canExpandPlot: false,
  selectedSceneryId: null,
  selectedSceneryIndex: null,
  selectedSceneryRotationDegrees: null,
  drafts: [],
  lastOpenedProjectId: null,
  recoveredDraft: null,
  onboarding: {
    creator: {
      active: false,
      step: null,
      status: 'not_started',
      finishOfferVisible: false,
    },
    finishLesson: { active: false, step: null, status: 'not_offered' },
    rider: { initialSeen: false, socialSeen: false },
  },
  telemetry: {
    speedMps: 0,
    speedMph: 0,
    verticalG: 1,
    lateralG: 0,
    longitudinalG: 0,
    peakSpeedMph: 0,
    activeSectionType: null,
    activeSectionLabel: 'Coasting',
  },
  ride: {
    intent: 'test',
    phase: 'idle',
    progressPercent: 0,
    elapsedSeconds: 0,
    dispatchSecondsRemaining: 0,
    peakVerticalG: 1,
    peakVerticalProgressPercent: 0,
    minVerticalG: 1,
    minVerticalProgressPercent: 0,
    peakLateralG: 0,
    peakLateralProgressPercent: 0,
    intensity: {
      level: 'gentle',
      label: 'Gentle',
      message: 'A relaxed ride with mild speed and forces.',
      score: 0,
      progressPercent: 0,
    },
    visitedSectionTypes: [],
    photoFlash: false,
  },
  lastRide: null,
  fullValidation: {
    pending: false,
    status: 'not_run',
    outcome: 'not_run',
    testPolicy: 'blocked',
    publishReady: false,
    technicalEligible: false,
    intensity: null,
    topSpeedMph: null,
    maxVerticalG: null,
    minVerticalG: null,
    maxAbsLateralG: null,
    maxAbsLongitudinalG: null,
    issues: [],
  },
  rideCheck: {
    status: 'checking',
    problemCount: 0,
    warningCount: 0,
    problems: [],
    warnings: [],
  },
  replay: {
    available: false,
    paused: false,
    elapsedSeconds: 0,
    durationSeconds: 0,
    progressPercent: 0,
  },
  media: {
    available: false,
    requiresNewRide: false,
    open: false,
    timelineSource: null,
    selectedAlternative: 0,
    editLabels: ['Best Moments', 'Biggest Thrills', 'Front Row', 'Fresh Mix'],
    videoAvailable: false,
    highlightMode: 'scenic',
    motionMomentCount: 0,
    photoSource: null,
    selectedPosterId: null,
    posterCandidates: [],
    creatorPosterSkipped: false,
    photoUrl: null,
    cardUrl: null,
    videoUrl: null,
    loopUrl: null,
    status: 'idle',
    progress: 0,
    includeAudio: true,
    includeReaction: false,
    includeReactionMicrophone: false,
    reactionAvailable: false,
    reactionMicrophoneAvailable: false,
    reactionRawUrl: null,
    reactionPlacement: 'top-right',
    reactionMirrored: false,
    imageCopyAvailable: false,
    publicLinkAvailable: false,
    formatLabel: null,
    message: 'Complete a ride to create a highlight.',
    shareOutcome: 'idle',
  },
  reaction: {
    eligibility: {
      state: 'platform_unavailable',
      reason: 'Reaction Cam is not enabled for this runtime.',
    },
    setupOpen: false,
    phase: 'idle',
    includeMicrophone: false,
    microphoneIncluded: false,
    exportMirrored: false,
    placement: 'top-right',
    devices: [],
    selectedDeviceId: null,
    previewStream: null,
    countdown: 0,
    recordingElapsedSeconds: 0,
    rawReviewUrl: null,
    rawAvailable: false,
    availableForExport: false,
    rideSpeedLocked: false,
    message: 'Reaction Cam is optional.',
    interruptionReason: null,
  },
  monetization: {
    availability: {
      state: 'platform_unavailable',
      reason: 'Optional styles are unavailable until Buncha Games monetization is connected.',
    },
    wallet: null,
    offers: [],
    passOwned: false,
    sessionGrants: [],
    selectedOfferId: null,
    transactionState: 'idle',
    message: 'The complete free media set is ready.',
    premiumIntentCount: 0,
    analytics: [],
    sandbox: false,
    passengerCamEligible: false,
    activeTreatments: {
      extraPhotoMoments: false,
      freshHighlightMix: false,
      alternateCamera: false,
      parkGlow: false,
      caption: 'none',
      styledLoop: false,
      passengerCam: false,
    },
    cheerPulse: 0,
  },
  settings: {
    cameraFov: 62,
    cameraShake: 0.25,
    reducedMotion: false,
    masterVolume: 0.75,
    musicVolume: 0.62,
    rideVolume: 0.85,
    windVolume: 0.7,
    environmentVolume: 0.58,
    uiVolume: 0.7,
    reactionVolume: 0.9,
    muted: false,
    audioState: 'locked',
    testSpeed: 1,
  },
  diagnostics: {
    qualityTier: 'high',
    frameMsP50: 0,
    frameMsP95: 0,
    inputMsP50: 0,
    inputMsP95: 0,
    rebuildMs: 0,
    changedSpans: 0,
    geometryRevision: 0,
    trackLengthM: 0,
    sampleCount: 0,
    rendererGeometries: 0,
    rendererTextures: 0,
    rendererDrawCalls: 0,
    rendererTriangles: 0,
    renderableCounts: '',
    renderableRoots: '',
    renderableBatches: '',
    trainRenderableTypes: '',
    rideCycles: 0,
    retainedTraceSamples: 0,
    retainedMediaSamples: 0,
    mediaArtifactUrls: 0,
    mediaExportActive: false,
    reactionActiveTracks: 0,
    reactionRecorderActive: false,
    reactionRetainedBytes: 0,
    validationActive: 0,
    validationQueued: 0,
    audioActiveVoices: 0,
    audioActiveLoops: 0,
    listenerCount: 0,
    maxRailSeamGapM: 0,
    maxRailSeamAngleDegrees: 0,
    maxTieSpacingM: 0,
  },
  debugVisible: false,
  persistenceAvailable: true,
  persistenceTier: 'durable',
};

function downloadBlob(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = filename;
  anchor.click();
  setTimeout(() => URL.revokeObjectURL(url), 0);
}

function mph(value: number): string {
  return Number.isFinite(value) ? `${value.toFixed(1)} mph` : '--';
}

function force(value: number): string {
  return Number.isFinite(value) ? `${value.toFixed(2)} G` : '--';
}

function TestSpeedControl({
  value,
  runtime,
  locked = false,
}: {
  readonly value: RuntimeSnapshot['settings']['testSpeed'];
  readonly runtime: CoasterRuntime | null;
  readonly locked?: boolean;
}) {
  return (
    <div class="test-speed-control" role="group" aria-label="Simulation speed">
      <span>Simulation Speed</span>
      {([0.5, 1, 2, 4] as const).map((speed) => (
        <button
          type="button"
          aria-pressed={value === speed}
          data-testid={`test-speed-${speed}`}
          disabled={locked}
          title={locked ? 'Recorded rides run at 1× for synchronization.' : undefined}
          onClick={() => runtime?.setTestSpeed(speed)}
        >
          {speed}×
        </button>
      ))}
      {locked && <small>Record Ride stays at 1×</small>}
    </div>
  );
}

export function App() {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const projectNameRef = useRef<HTMLInputElement>(null);
  const runtimeRef = useRef<CoasterRuntime | null>(null);
  const [state, setState] = useState<RuntimeSnapshot>(INITIAL_SNAPSHOT);
  const holdLoadingMark =
    import.meta.env.MODE !== 'production' &&
    new URLSearchParams(window.location.search).get('loadingReview') === '1';
  const previousSaveState = useRef(state.saveState);
  const [reactionUi, setReactionUi] = useState<typeof ReactionCamUi | null>(null);
  const [helpOpen, setHelpOpen] = useState(false);
  const [compactViewport, setCompactViewport] = useState(
    () => globalThis.matchMedia?.('(max-width: 720px)').matches ?? false,
  );

  useEffect(() => {
    const media = globalThis.matchMedia?.('(max-width: 720px)');
    if (!media) return;
    const update = () => setCompactViewport(media.matches);
    update();
    media.addEventListener('change', update);
    return () => media.removeEventListener('change', update);
  }, []);

  useEffect(() => {
    if (previousSaveState.current !== 'error' && state.saveState === 'error')
      runtimeRef.current?.playUiFeedback('system_failure');
    previousSaveState.current = state.saveState;
  }, [state.saveState]);
  const [confirmExit, setConfirmExit] = useState(false);
  const [exitDestination, setExitDestination] = useState<ExitDestination>('main_menu');
  const [confirmPublishSave, setConfirmPublishSave] = useState(false);
  const [topbarMenuOpen, setTopbarMenuOpen] = useState(false);
  const [recordRideCamera, setRecordRideCamera] = useState<'first-person' | 'third-person' | null>(
    null,
  );
  const [coordinateRecoveryEpoch, setCoordinateRecoveryEpoch] = useState(0);
  const [draftsOpen, setDraftsOpen] = useState(false);
  const [hubView, setHubView] = useState<HubView>('home');
  const [catalogPage, setCatalogPage] = useState<DiscoveryCatalogPage | null>(null);
  const [catalogSort, setCatalogSort] = useState<PublicCatalogSort>('most_boosted');
  const [browseCollection, setBrowseCollection] = useState<BrowseCollection>('most_boosted');
  const [remixableOnly, setRemixableOnly] = useState(false);
  const [catalogCreator, setCatalogCreator] = useState<{ id: string; label: string } | null>(null);
  const [catalogLoading, setCatalogLoading] = useState(false);
  const [catalogError, setCatalogError] = useState<string | null>(null);
  const [catalogMessage, setCatalogMessage] = useState<string | null>(null);
  const [socialPendingIds, setSocialPendingIds] = useState<ReadonlySet<string>>(new Set());
  const [myCoasters, setMyCoasters] = useState<readonly MyCoasterSummary[]>([]);
  const [myCoastersTab, setMyCoastersTab] = useState<'published' | 'drafts'>('published');
  const [myCoastersLoading, setMyCoastersLoading] = useState(false);
  const [myCoastersError, setMyCoastersError] = useState<string | null>(null);
  const [draftPreviewUrls, setDraftPreviewUrls] = useState<Readonly<Record<string, string>>>({});
  const draftPreviewUrlsRef = useRef<Readonly<Record<string, string>>>({});
  const [confirmDeleteProjectId, setConfirmDeleteProjectId] = useState<string | null>(null);
  const [renameValue, setRenameValue] = useState<string | null>(null);
  const pendingProjectNameRef = useRef<string | null>(null);
  const localQaContext = useMemo(
    () =>
      import.meta.env.MODE !== 'production' ? readLocalQaUrlContext(window.location.search) : null,
    [],
  );
  const debugControlsAvailable = import.meta.env.MODE !== 'production' || state.debugVisible;
  const catalogHoverQaScenario = useMemo(
    () =>
      import.meta.env.MODE !== 'production'
        ? new URLSearchParams(window.location.search).get('catalogHoverScenario')
        : null,
    [],
  );
  const draftPersistenceScope = useMemo<DraftPersistenceScope | undefined>(
    () =>
      localQaContext
        ? localQaDraftPersistenceScope(
            localQaContext.session,
            localQaContext.viewer,
            localQaContext.viewer === 'qa.alice' ? 'Alice QA' : 'Bob QA',
          )
        : undefined,
    [localQaContext],
  );
  const draftPreviewRepository = useMemo(
    () => new DraftPreviewRepository(draftPersistenceScope),
    [draftPersistenceScope],
  );
  const publicBaseUrl = useMemo(
    () =>
      resolvePublicBaseUrl({
        currentHref: window.location.href,
        configuredBaseUrl:
          import.meta.env.VITE_BUNCHA_PUBLIC_URL ||
          new URL(import.meta.env.BASE_URL, window.location.origin).toString(),
        localQa: localQaContext,
      }),
    [localQaContext],
  );
  const editorResetPath = useMemo(
    () =>
      buildEditorResetPath({
        currentHref: window.location.href,
        basePath: import.meta.env.BASE_URL,
        localQa: localQaContext,
      }),
    [localQaContext],
  );
  const platform = useMemo(
    () =>
      createPhase4PlatformAdapter({
        apiBaseUrl: import.meta.env.VITE_COASTERS_API_URL || window.location.origin,
        buncha: window.buncha,
        allowLocalQa: import.meta.env.MODE !== 'production',
      }),
    [],
  );
  const worldLaunchSession = useMemo(() => new WorldLaunchSession(window.buncha?.world), []);
  const [worldLaunchPending, setWorldLaunchPending] = useState(() => Boolean(window.buncha?.world));
  const worldHubAvailable = Boolean(window.buncha?.world?.openContentHub);
  const monetizationAdapter = useMemo(() => {
    const params = new URLSearchParams(window.location.search);
    const enabled =
      import.meta.env.MODE !== 'production' &&
      params.get('coastersMonetization') === 'local-sandbox';
    if (!enabled) return createUnavailableMonetizationAdapter();
    const scenario = (params.get('monetizationScenario') ?? 'success') as LocalSandboxScenario;
    return createLocalSandboxMonetizationAdapter({
      viewerId: localQaContext?.viewer ?? 'local-device',
      initialBalance: Number(params.get('qaCredits') ?? 500),
      scenario,
    });
  }, [localQaContext]);
  const [viewer, setViewer] = useState<CoasterViewer>({
    kind: 'anonymous',
    id: null,
    displayName: 'Guest',
  });
  const publishWorkspaceOwnerId = viewer.kind === 'authenticated' ? viewer.id : 'local-device';
  const publishWorkspaceRepository = useMemo(
    () =>
      new CreatorPublishWorkspaceRepository(
        createNamedProjectRepository({ scope: draftPersistenceScope }),
        publishWorkspaceOwnerId,
      ),
    [draftPersistenceScope, publishWorkspaceOwnerId],
  );
  const [cloudDrafts, setCloudDrafts] = useState<readonly CloudDraftSummary[]>([]);
  const [cloudRecord, setCloudRecord] = useState<CloudDraftRecord | null>(null);
  const [publishStatus, setPublishStatus] = useState<PublishStatusRecord | null>(null);
  const [publishOpen, setPublishOpen] = useState(false);
  const [shareDraftOpen, setShareDraftOpen] = useState(false);
  const [shareDraftStale, setShareDraftStale] = useState(false);
  const [shareRidePreparing, setShareRidePreparing] = useState(false);
  const [publishSessionId, setPublishSessionId] = useState(0);
  const [publishHydration, setPublishHydration] = useState<PublishHydrationState>('idle');
  const [publishProject, setPublishProject] = useState<CoasterProject | null>(null);
  const [publishMessage, setPublishMessage] = useState<string | null>(null);
  const [publishBusy, setPublishBusy] = useState(false);
  const [publishSubmitting, setPublishSubmitting] = useState(false);
  const [cloudConflict, setCloudConflict] = useState<PublishConflictState | null>(null);
  const [heroCapture, setHeroCapture] = useState<HeroCaptureBundle | null>(null);
  const [heroPreviewUrl, setHeroPreviewUrl] = useState<string | null>(null);
  const [heroCaptureState, setHeroCaptureState] = useState<'none' | 'captured' | 'stale'>('none');
  const [catalogHoverArtifact, setCatalogHoverArtifact] =
    useState<CatalogHoverVideoArtifact | null>(null);
  const [catalogHoverPreviewUrl, setCatalogHoverPreviewUrl] = useState<string | null>(null);
  const [catalogHoverState, setCatalogHoverState] =
    useState<CatalogHoverCaptureState>('waiting_for_preview');
  const [catalogHoverProgress, setCatalogHoverProgress] = useState(0);
  const [readOnlyShare, setReadOnlyShare] = useState<ReadOnlyShare | null>(null);
  const [publishWorkspace, setPublishWorkspace] = useState<CreatorPublishWorkspaceV1 | null>(null);
  const [visitor, setVisitor] = useState<
    | { kind: 'public'; record: PublicCoasterRecord }
    | { kind: 'unlisted'; record: ReadOnlyShareRecord }
    | { kind: 'private'; record: ReadOnlyShareRecord }
    | null
  >(null);
  const [activePublicCard, setActivePublicCard] = useState<PublicCoasterCard | null>(null);
  const [activeRemixSource, setActiveRemixSource] = useState<PublicCoasterCard | null>(null);
  const [launchMessage, setLaunchMessage] = useState<string | null>(null);
  const [pendingWorldLaunch, setPendingWorldLaunch] = useState<BunchaWorldLaunchContext | null>(
    null,
  );
  const [pendingPrivateDraftRevision, setPendingPrivateDraftRevision] = useState<string | null>(
    null,
  );
  const launchAttempted = useRef(false);
  const referralRide = useRef<{
    referralId: string | null;
    rideSessionId: string | null;
    started: boolean;
    completed: boolean;
  } | null>(null);
  const lastCloudSyncedEditorRevision = useRef<number | null>(null);
  const publishHydrationToken = useRef(0);
  const cloudRecordRef = useRef<CloudDraftRecord | null>(null);
  const preparedShareRideRef = useRef<{
    projectId: string;
    fingerprint: string;
    cloud: CloudDraftRecord;
  } | null>(null);
  const readOnlyShareFingerprintRef = useRef<string | null>(null);
  const readOnlyShareTestedAtRef = useRef<string | null>(null);
  const readOnlyShareRef = useRef<ReadOnlyShare | null>(null);
  const shareRideRotationRef = useRef<Promise<ReadOnlyShare | null> | null>(null);
  const shareRidePreparationRef = useRef<{
    projectId: string;
    fingerprint: string;
    promise: Promise<CloudDraftRecord | null>;
  } | null>(null);
  const cloudWriteTail = useRef<Promise<void>>(Promise.resolve());
  const publishWorkspaceWriteTail = useRef<Promise<void>>(Promise.resolve());
  const catalogHoverArtifactRef = useRef<CatalogHoverVideoArtifact | null>(null);
  const catalogHoverCapturePromise = useRef<Promise<CatalogHoverVideoArtifact | null> | null>(null);
  const catalogHoverAbort = useRef<AbortController | null>(null);
  const publishSubmissionPromise = useRef<Promise<void> | null>(null);
  const publishIdempotency = useRef<{ operation: string; key: string } | null>(null);
  const enqueueCloudWrite = useCallback(<T,>(operation: () => Promise<T>): Promise<T> => {
    const queued = cloudWriteTail.current.then(operation, operation);
    cloudWriteTail.current = queued.then(
      () => undefined,
      () => undefined,
    );
    return queued;
  }, []);
  const inLiveRide = state.mode === 'ride-first-person' || state.mode === 'ride-third-person';
  const inReplay = state.mode === 'replay';
  const inRide = inLiveRide || inReplay;
  const inEditor = state.mode === 'editor';
  const camera = state.mode === 'ride-third-person' ? 'third-person' : 'first-person';
  const waitingForDispatch =
    inLiveRide && state.telemetry.activeSectionType === 'station' && state.telemetry.speedMps < 0.3;
  const reactionCamAvailable = state.reaction.eligibility.state === 'available';
  const shareDraftCapability = platform.getCapability('read_only_shares');
  const shareDraftAvailable =
    viewer.kind === 'authenticated' && shareDraftCapability.availability === 'available';
  const shareRideAllowed = shareDraftAvailable && state.recording.allowed;
  const creatorGuideCompletionPending = state.onboarding.creator.step === 'unlocks';

  useEffect(() => {
    if (reactionUi || (!state.reaction.setupOpen && state.reaction.phase !== 'recording')) return;
    let cancelled = false;
    void import('./ReactionCam.tsx').then((module) => {
      if (!cancelled) setReactionUi(module);
    });
    return () => {
      cancelled = true;
    };
  }, [reactionUi, state.reaction.phase, state.reaction.setupOpen]);

  useEffect(() => {
    if (!canvasRef.current) return;
    const runtime = createCoasterRuntime(canvasRef.current, {
      persistenceScope: draftPersistenceScope,
      reactionCamPlatformAvailable:
        import.meta.env.MODE !== 'production' ||
        platform.getCapability('camera').availability === 'available',
      reactionCamEligibleAccount: true,
      reactionCamSynthetic:
        import.meta.env.MODE !== 'production' &&
        new URLSearchParams(window.location.search).get('reactionCam') === 'synthetic',
      monetizationAdapter,
    });
    runtimeRef.current = runtime;
    const unsubscribe = runtime.subscribe(setState);
    return () => {
      unsubscribe();
      runtime.dispose();
      runtimeRef.current = null;
    };
  }, [draftPersistenceScope, monetizationAdapter, platform]);

  useEffect(() => {
    let cancelled = false;
    const loadPreviews = async () => {
      const entries = await Promise.all(
        state.drafts
          .filter((draft) => !draft.corrupt)
          .map(
            async (draft) =>
              [draft.projectId, await draftPreviewRepository.load(draft.projectId)] as const,
          ),
      );
      if (cancelled) return;
      const next: Record<string, string> = {};
      for (const [projectId, blob] of entries)
        if (blob) next[projectId] = URL.createObjectURL(blob);
      setDraftPreviewUrls((current) => {
        Object.values(current).forEach((url) => URL.revokeObjectURL(url));
        draftPreviewUrlsRef.current = next;
        return next;
      });
    };
    void loadPreviews();
    return () => {
      cancelled = true;
    };
  }, [draftPreviewRepository, state.drafts]);

  useEffect(
    () => () => {
      draftPreviewRepository.dispose();
      Object.values(draftPreviewUrlsRef.current).forEach((url) => URL.revokeObjectURL(url));
    },
    [draftPreviewRepository],
  );

  const hasPendingNameChange = renameValue !== null && renameValue !== state.fixtureTitle;

  useEffect(() => {
    if ((!state.hasUnsavedChanges && !hasPendingNameChange) || !inEditor || visitor) return;
    const warnBeforeUnload = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      event.returnValue = '';
    };
    window.addEventListener('beforeunload', warnBeforeUnload);
    return () => window.removeEventListener('beforeunload', warnBeforeUnload);
  }, [hasPendingNameChange, inEditor, state.hasUnsavedChanges, visitor]);

  useEffect(() => {
    let cancelled = false;
    void worldLaunchSession.initialize().then(async (launch) => {
      if (cancelled || launch.kind === 'private') return;
      const result = await platform.getViewer();
      if (cancelled || !result.ok) return;
      setViewer(result.value);
      if (result.value.kind === 'authenticated') {
        const drafts = await platform.listCloudDrafts();
        if (!cancelled && drafts.ok) setCloudDrafts(drafts.value);
      }
    });
    return () => {
      cancelled = true;
    };
  }, [platform, worldLaunchSession]);

  useEffect(() => {
    if (!publishOpen || publishHydration === 'loading' || !runtimeRef.current) return;
    setPublishProject(runtimeRef.current.getProjectSnapshot());
  }, [publishHydration, publishOpen, state.editorRevision]);

  useEffect(() => {
    return () => {
      if (heroPreviewUrl) URL.revokeObjectURL(heroPreviewUrl);
    };
  }, [heroPreviewUrl]);

  useEffect(() => {
    if (!state.ready || !runtimeRef.current || launchAttempted.current) return;
    launchAttempted.current = true;
    const url = new URL(window.location.href);
    const basePath = import.meta.env.BASE_URL.replace(/\/$/, '');
    const routePath =
      basePath && url.pathname.startsWith(`${basePath}/`)
        ? url.pathname.slice(basePath.length)
        : url.pathname;
    const shareMatch = routePath.match(/^\/coasters\/share\/([^/]+)$/);
    const strictShare = new URLSearchParams(url.hash.slice(1)).get('share');
    const strictSeparator = strictShare?.indexOf('.') ?? -1;
    const strictShareId =
      strictShare && strictSeparator > 0 ? strictShare.slice(0, strictSeparator) : null;
    const strictSecret =
      strictShare && strictSeparator > 0 ? strictShare.slice(strictSeparator + 1) : null;
    const load = async () => {
      try {
        const worldLaunch = await worldLaunchSession.initialize();
        if (worldLaunch.kind === 'blocked') {
          setLaunchMessage(worldLaunch.reason);
          return;
        }
        if (worldLaunch.kind === 'direct') {
          setLaunchMessage('Loading coaster…');
          const result = await platform.getPublicCoaster({
            publicCoasterId: worldLaunch.context.publicCreationId,
          });
          if (!result.ok) {
            setLaunchMessage('This coaster is private, unavailable, or no longer published.');
            return;
          }
          setLaunchMessage(`Loading ${result.value.title}…`);
          if (!matchesWorldLaunch(result.value, worldLaunch.context)) {
            setLaunchMessage(
              'This coaster revision does not match the signed Buncha Games launch. Please reopen it from Buncha Games.',
            );
            return;
          }
          runtimeRef.current?.openEditor();
          await runtimeRef.current?.loadProject(result.value.project, 'readonly_public');
          if (
            runtimeRef.current?.getProjectSnapshot().revisionId !== result.value.project.revisionId
          ) {
            setLaunchMessage('The requested immutable coaster revision could not be loaded.');
            return;
          }
          setVisitor({ kind: 'public', record: result.value });
          referralRide.current = {
            referralId: url.searchParams.get('ref') ?? url.searchParams.get('shareId'),
            rideSessionId: null,
            started: false,
            completed: false,
          };
          setPendingWorldLaunch(worldLaunch.context);
          setLaunchMessage(null);
          void (async () => {
            const socialPage = await platform.listPublicCoasters({
              sort: 'newest',
              remixableOnly: false,
              creatorId: result.value.creator.publicCreatorId,
              limit: 6,
            });
            const viewerStates = socialPage.ok
              ? await platform.getViewerCoasterStates(
                  socialPage.value.items.map((card) => card.publicCoasterId),
                )
              : null;
            const discoveryPage = socialPage.ok
              ? mergeDiscoveryCatalog(
                  socialPage.value,
                  viewerStates?.ok ? viewerStates.value : null,
                )
              : null;
            setActivePublicCard(
              discoveryPage
                ? (discoveryPage.items.find(
                    (card) => card.publicCoasterId === result.value.publicCoasterId,
                  ) ?? null)
                : null,
            );
          })();
          return;
        }
        if (worldLaunch.kind === 'private') {
          setLaunchMessage('Loading private Coaster Ride…');
          runtimeRef.current?.beginPrivateReadOnlyLaunch();
          const result = await platform.resolvePrivateDraftLaunch();
          if (!result.ok) {
            setLaunchMessage('This private Coaster Ride is unavailable or has been revoked.');
            return;
          }
          setLaunchMessage(`Loading ${result.value.title}…`);
          await runtimeRef.current?.loadProject(result.value.project, 'readonly_private');
          if (runtimeRef.current?.getProjectSnapshot().revisionId !== result.value.revisionId) {
            setLaunchMessage('The requested immutable Coaster snapshot could not be loaded.');
            return;
          }
          runtimeRef.current?.openEditor();
          setVisitor({ kind: 'private', record: result.value });
          setPendingPrivateDraftRevision(result.value.revisionId);
          setLaunchMessage(null);
          return;
        }
        if (shareMatch || strictShareId) {
          setLaunchMessage('Loading coaster…');
          const shareId = strictShareId ?? decodeURIComponent(shareMatch?.[1] ?? '');
          const secret = strictSecret ?? new URLSearchParams(url.hash.slice(1)).get('cap');
          if (!secret) {
            setLaunchMessage('This private link is incomplete. Ask the creator for the full link.');
            return;
          }
          const result = await platform.loadReadOnlyShare(shareId, secret);
          if (!result.ok) {
            setLaunchMessage('This private coaster link is unavailable or has been revoked.');
            return;
          }
          setLaunchMessage(`Loading ${result.value.title}…`);
          runtimeRef.current?.openEditor();
          await runtimeRef.current?.loadProject(result.value.project, 'readonly_private');
          setVisitor({ kind: 'unlisted', record: result.value });
          setLaunchMessage(null);
        }
      } catch {
        setLaunchMessage('This Coaster could not be opened.');
      } finally {
        setWorldLaunchPending(false);
      }
    };
    void load();
  }, [platform, state.ready, worldLaunchSession]);

  useEffect(() => {
    if (
      !pendingPrivateDraftRevision ||
      visitor?.kind !== 'private' ||
      visitor.record.revisionId !== pendingPrivateDraftRevision ||
      state.mode !== 'editor' ||
      state.fullValidation.pending
    )
      return;
    worldLaunchSession.privateDraftReady();
    setPendingPrivateDraftRevision(null);
  }, [
    pendingPrivateDraftRevision,
    state.fullValidation.pending,
    state.mode,
    visitor,
    worldLaunchSession,
  ]);

  useEffect(() => {
    if (
      !pendingWorldLaunch ||
      visitor?.kind !== 'public' ||
      visitor.record.publicCoasterId !== pendingWorldLaunch.publicCreationId ||
      state.mode !== 'editor' ||
      state.fullValidation.pending
    )
      return;
    if (!state.fullValidation.technicalEligible) {
      setLaunchMessage('This exact coaster revision loaded, but it is not currently rideable.');
      setPendingWorldLaunch(null);
      return;
    }
    worldLaunchSession.creationReady();
    setPendingWorldLaunch(null);
    setLaunchMessage(null);
    runtimeRef.current?.startTestRide('first-person');
  }, [
    pendingWorldLaunch,
    state.fullValidation.pending,
    state.fullValidation.technicalEligible,
    state.mode,
    visitor,
    worldLaunchSession,
  ]);

  useEffect(() => {
    const referral = referralRide.current;
    if (!referral || referral.started || state.ride.phase !== 'running') return;
    referral.started = true;
    void platform
      .startQualifiedRide({ ...(referral.referralId ? { referralId: referral.referralId } : {}) })
      .then((result) => {
        if (result.ok) referral.rideSessionId = result.value.rideSessionId;
      });
  }, [platform, state.ride.phase]);

  useEffect(() => {
    const referral = referralRide.current;
    if (
      !referral ||
      referral.completed ||
      !referral.rideSessionId ||
      state.lastRide?.outcome !== 'completed'
    )
      return;
    referral.completed = true;
    void platform.completeQualifiedRide({ rideSessionId: referral.rideSessionId });
  }, [platform, state.lastRide?.outcome]);

  useEffect(() => {
    if (state.mode !== 'editor') return;
    const onKeyDown = (event: KeyboardEvent) => {
      const target = event.target as HTMLElement | null;
      if (target?.matches('input, textarea, select')) return;
      if (
        (event.key === 'Enter' || event.key === ' ') &&
        target?.closest('button, a, [role="button"], [role="menuitem"]')
      )
        return;
      const creatorStep = state.onboarding.creator.step;
      const finishStep = state.onboarding.finishLesson.step;
      const guideActive = Boolean(creatorStep || finishStep);
      if (helpOpen && event.key === 'Escape') {
        event.preventDefault();
        event.stopImmediatePropagation();
        setHelpOpen(false);
      } else if (state.stationPlacementMode.active) {
        const key = event.key.toLowerCase();
        if (key === 'escape') runtimeRef.current?.cancelStationPlacement();
        else if (key === 'enter') runtimeRef.current?.commitStationPlacement();
        else if (key === 'q') runtimeRef.current?.rotateStationPlacement(-1);
        else if (key === 'e') runtimeRef.current?.rotateStationPlacement(1);
        else if (key === 'arrowleft') runtimeRef.current?.nudgeStationPlacement(-1, 0);
        else if (key === 'arrowright') runtimeRef.current?.nudgeStationPlacement(1, 0);
        else if (key === 'arrowup') runtimeRef.current?.nudgeStationPlacement(0, -1);
        else if (key === 'arrowdown') runtimeRef.current?.nudgeStationPlacement(0, 1);
        else return;
        event.preventDefault();
      } else if (guideActive && event.key !== 'Escape') {
        const placeStep =
          [
            'first_piece',
            'powered_transition',
            'powered_climb',
            'level_off_place',
            'turn_back_place',
            'downhill_transition',
            'downhill_run',
            'downhill_run_finish',
            'downhill_level_place',
            'barrel_roll_place',
            'photo_place',
            'bank_home_place',
            'turn_home_place',
            'bank_level_place',
            'brake_run',
          ].includes(creatorStep ?? '') || finishStep === 'close_track';
        if (placeStep && event.key === 'Enter' && state.preview?.canAccept) {
          event.preventDefault();
          runtimeRef.current?.acceptPreview();
        } else if (
          finishStep === 'test_ride' &&
          event.key.toLowerCase() === 't' &&
          state.testAllowed
        ) {
          event.preventDefault();
          runtimeRef.current?.startTestRide('first-person');
        } else if (
          event.ctrlKey ||
          event.metaKey ||
          ['Delete', 'Backspace', '[', ']'].includes(event.key) ||
          ['t', 'f'].includes(event.key.toLowerCase())
        ) {
          event.preventDefault();
        }
      } else if ((event.ctrlKey || event.metaKey) && event.key.toLowerCase() === 'z') {
        event.preventDefault();
        if (event.shiftKey) runtimeRef.current?.redo();
        else runtimeRef.current?.undo();
      } else if ((event.ctrlKey || event.metaKey) && event.key.toLowerCase() === 'y') {
        event.preventDefault();
        runtimeRef.current?.redo();
      } else if (event.key === 'Delete' || event.key === 'Backspace') {
        event.preventDefault();
        runtimeRef.current?.deleteSelected();
      } else if (event.key.toLowerCase() === 't' && state.testAllowed) {
        event.preventDefault();
        runtimeRef.current?.startTestRide('first-person');
      } else if (event.key === 'Escape') {
        event.preventDefault();
        if (state.hasUnsavedChanges || hasPendingNameChange) setConfirmExit(true);
        else returnToMainMenu();
      } else if (event.key === 'Enter' && state.preview?.canAccept && !target?.matches('button')) {
        event.preventDefault();
        runtimeRef.current?.acceptPreview();
      } else if (event.key === '[') {
        runtimeRef.current?.traverseSelection(-1);
      } else if (event.key === ']') {
        runtimeRef.current?.traverseSelection(1);
      } else if (event.key.toLowerCase() === 'f') {
        runtimeRef.current?.focusSelection();
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [
    hasPendingNameChange,
    helpOpen,
    state.hasUnsavedChanges,
    state.mode,
    state.onboarding.creator.step,
    state.onboarding.finishLesson.step,
    state.preview?.canAccept,
    state.stationPlacementMode.active,
    state.testAllowed,
  ]);

  const refreshCloudDrafts = async () => {
    const result = await platform.listCloudDrafts();
    if (result.ok) setCloudDrafts(result.value);
  };

  const loadCatalog = useCallback(
    async (append = false) => {
      setCatalogLoading(true);
      setCatalogError(null);
      const cursor = append ? (catalogPage?.nextCursor ?? undefined) : undefined;
      const query = {
        sort: catalogSort,
        remixableOnly,
        creatorId: catalogCreator?.id,
        cursor,
        limit: 6,
      };
      const result =
        browseCollection === 'favorites'
          ? await platform.listFavoriteCoasters(query)
          : await platform.listPublicCoasters(query);
      if (!result.ok) {
        setCatalogError(result.error.message);
        setCatalogLoading(false);
        return;
      }
      const viewerStates =
        viewer.kind === 'authenticated'
          ? await platform.getViewerCoasterStates(
              result.value.items.map((card) => card.publicCoasterId),
            )
          : null;
      const merged = mergeDiscoveryCatalog(
        result.value,
        viewerStates?.ok ? viewerStates.value : null,
      );
      setCatalogPage((current) =>
        append && current ? { ...merged, items: [...current.items, ...merged.items] } : merged,
      );
      setCatalogLoading(false);
    },
    [
      browseCollection,
      catalogCreator?.id,
      catalogPage?.nextCursor,
      catalogSort,
      platform,
      remixableOnly,
      viewer.kind,
    ],
  );

  const loadMyCoasters = useCallback(async () => {
    if (viewer.kind !== 'authenticated') return;
    setMyCoastersLoading(true);
    setMyCoastersError(null);
    const published = await platform.listMyCoasters();
    if (published.ok) setMyCoasters(published.value);
    else setMyCoastersError(published.error.message);
    setMyCoastersLoading(false);
  }, [platform, viewer.kind]);

  useEffect(() => {
    if (state.mode !== 'menu') return;
    if (hubView === 'browse') void loadCatalog(false);
    if (hubView === 'my-coasters') void loadMyCoasters();
  }, [browseCollection, catalogCreator?.id, catalogSort, hubView, remixableOnly, state.mode]);

  const updateCatalogCard = useCallback(
    (publicCoasterId: string, update: (card: PublicCoasterCard) => PublicCoasterCard) => {
      setCatalogPage((page) =>
        page
          ? {
              ...page,
              items: page.items.map((card) =>
                card.publicCoasterId === publicCoasterId ? update(card) : card,
              ),
            }
          : page,
      );
      setActivePublicCard((card) =>
        card?.publicCoasterId === publicCoasterId ? update(card) : card,
      );
    },
    [],
  );

  const mutateSocial = useCallback(
    async (card: PublicCoasterCard, kind: 'boost' | 'favorite') => {
      if (viewer.kind !== 'authenticated') {
        setCatalogMessage('Sign in through Buncha Games to use Boosts and Favorites.');
        return;
      }
      if (card.viewerState.isOwner) {
        setCatalogMessage('You made this coaster, so its ranking stays organic.');
        return;
      }
      const desired = kind === 'boost' ? !card.viewerState.boosted : !card.viewerState.favorited;
      setSocialPendingIds((current) => new Set([...current, card.publicCoasterId]));
      updateCatalogCard(card.publicCoasterId, (current) => ({
        ...current,
        viewerState: {
          ...current.viewerState,
          [kind === 'boost' ? 'boosted' : 'favorited']: desired,
        },
        stats: {
          ...current.stats,
          ...(kind === 'boost'
            ? { boosts: Math.max(0, current.stats.boosts + (desired ? 1 : -1)) }
            : current.stats.favorites === undefined
              ? {}
              : { favorites: Math.max(0, current.stats.favorites + (desired ? 1 : -1)) }),
        },
      }));
      const result = await (kind === 'boost'
        ? platform.setBoost({
            publicCoasterId: card.publicCoasterId,
            desired,
            idempotencyKey: crypto.randomUUID(),
          })
        : platform.setFavorite({
            publicCoasterId: card.publicCoasterId,
            desired,
            idempotencyKey: crypto.randomUUID(),
          }));
      if (!result.ok) {
        updateCatalogCard(card.publicCoasterId, () => card);
        setCatalogMessage(
          `${kind === 'boost' ? 'Boost' : 'Favorite'} did not save. ${result.error.message}`,
        );
      } else {
        updateCatalogCard(card.publicCoasterId, (current) => ({
          ...current,
          stats: result.value.authoritativeCounts,
          viewerState: {
            ...current.viewerState,
            boosted: result.value.boosted,
            favorited: result.value.favorited,
          },
        }));
        if (kind === 'favorite' && browseCollection === 'favorites' && !result.value.favorited) {
          setCatalogPage((page) =>
            page
              ? {
                  ...page,
                  items: page.items.filter((item) => item.publicCoasterId !== card.publicCoasterId),
                }
              : page,
          );
        }
        setCatalogMessage(
          kind === 'boost'
            ? result.value.boosted
              ? 'Boost added. Popularity cannot be purchased.'
              : 'Boost removed.'
            : result.value.favorited
              ? 'Saved to My Coasters.'
              : 'Removed from Favorites.',
        );
      }
      setSocialPendingIds((current) => {
        const next = new Set(current);
        next.delete(card.publicCoasterId);
        return next;
      });
    },
    [browseCollection, platform, updateCatalogCard, viewer.kind],
  );

  const openPublicCard = useCallback(
    async (card: PublicCoasterCard) => {
      setActiveRemixSource(null);
      setCatalogMessage('Loading coaster…');
      const result = await platform.getPublicCoaster({ publicCoasterId: card.publicCoasterId });
      if (!result.ok) {
        setCatalogMessage(result.error.message);
        return;
      }
      runtimeRef.current?.openEditor();
      await runtimeRef.current?.loadProject(result.value.project, 'readonly_public');
      setVisitor({ kind: 'public', record: result.value });
      setActivePublicCard(card);
      setCatalogMessage(null);
      const destination = buildPublicCoasterUrl({
        publicSlug: card.publicSlug,
        publicBaseUrl,
        localQa: localQaContext,
      });
      const parsed = new URL(destination);
      history.replaceState(null, '', `${parsed.pathname}${parsed.search}${parsed.hash}`);
    },
    [localQaContext, platform, publicBaseUrl],
  );

  const openPublicSlug = useCallback(
    async (publicSlug: string) => {
      setActiveRemixSource(null);
      const result = await platform.getPublicCoaster({ publicSlug });
      if (!result.ok) {
        setMyCoastersError(result.error.message);
        return;
      }
      runtimeRef.current?.openEditor();
      await runtimeRef.current?.loadProject(result.value.project, 'readonly_public');
      setVisitor({ kind: 'public', record: result.value });
      const socialPage = await platform.listPublicCoasters({
        sort: 'most_boosted',
        remixableOnly: false,
        creatorId: result.value.creator.publicCreatorId,
        limit: 18,
      });
      if (socialPage.ok) {
        const viewerStates =
          viewer.kind === 'authenticated'
            ? await platform.getViewerCoasterStates(
                socialPage.value.items.map((card) => card.publicCoasterId),
              )
            : null;
        const discoveryPage = mergeDiscoveryCatalog(
          socialPage.value,
          viewerStates?.ok ? viewerStates.value : null,
        );
        setActivePublicCard(
          discoveryPage.items.find(
            (card) => card.publicCoasterId === result.value.publicCoasterId,
          ) ?? null,
        );
      } else {
        setActivePublicCard(null);
      }
      const destination = buildPublicCoasterUrl({
        publicSlug,
        publicBaseUrl,
        localQa: localQaContext,
      });
      const parsed = new URL(destination);
      history.replaceState(null, '', `${parsed.pathname}${parsed.search}${parsed.hash}`);
    },
    [localQaContext, platform, publicBaseUrl],
  );

  const remixPublicCard = useCallback(
    async (card: PublicCoasterCard) => {
      setSocialPendingIds((current) => new Set([...current, card.publicCoasterId]));
      setCatalogMessage('Creating your remix…');
      try {
        const result = await platform.remixCoaster({ publicCoasterId: card.publicCoasterId });
        if (!result.ok) {
          setCatalogMessage(result.error.message);
          return;
        }
        runtimeRef.current?.openEditor();
        await runtimeRef.current?.loadProject(result.value.draft.project, 'editable_remix');
        setActiveRemixSource(card);
        setCloudRecord(result.value.draft);
        setPublishStatus(null);
        setVisitor(null);
        setActivePublicCard(null);
        referralRide.current = null;
        setCatalogMessage(null);
        history.replaceState(null, '', editorResetPath);
      } finally {
        setSocialPendingIds((current) => {
          const next = new Set(current);
          next.delete(card.publicCoasterId);
          return next;
        });
      }
    },
    [editorResetPath, platform],
  );

  const copyReferralLink = useCallback(
    async (card: PublicCoasterCard) => {
      const fallback = buildPublicCoasterUrl({
        publicSlug: card.publicSlug,
        publicBaseUrl,
        localQa: localQaContext,
      });
      const referral = await platform.createReferralLink(card.publicCoasterId);
      const url = new URL(fallback);
      if (referral.ok) url.searchParams.set('ref', referral.value.referralId);
      try {
        await navigator.clipboard.writeText(url.toString());
        setCatalogMessage(referral.ok ? 'Referral ride link copied.' : 'Ride link copied.');
        setLaunchMessage(referral.ok ? 'Referral ride link copied.' : 'Ride link copied.');
      } catch {
        setCatalogMessage('Copy was blocked. Open the coaster and copy the address instead.');
        setLaunchMessage('Copy was blocked. Copy the address from your browser instead.');
      }
    },
    [localQaContext, platform, publicBaseUrl],
  );

  const refreshPublishStatus = async (projectId: string, announce = false) => {
    const result = await platform.getPublishStatus(projectId);
    if (!result.ok) {
      if (announce) setPublishMessage(result.error.message);
      return null;
    }
    setPublishStatus(result.value);
    setCloudRecord((current) =>
      current?.projectId === projectId
        ? {
            ...current,
            state: result.value.state,
            pendingRevisionId: result.value.pendingRevisionId,
            currentPublicRevisionId: result.value.currentPublicRevisionId,
            publicCoasterId: result.value.publicCoasterId,
            publicSlug: result.value.publicSlug,
          }
        : current,
    );
    if (announce) setPublishMessage('Publish status refreshed.');
    await refreshCloudDrafts();
    return result.value;
  };

  useEffect(() => {
    const projectId = cloudRecord?.projectId;
    if (
      !publishOpen ||
      !projectId ||
      !publishStatus ||
      ownerProjectStatusLabel(publishStatus.state) !== 'pending_review'
    )
      return;
    const timer = window.setInterval(() => {
      void refreshPublishStatus(projectId);
    }, 15_000);
    return () => window.clearInterval(timer);
  }, [cloudRecord?.projectId, publishOpen, publishStatus?.state]);

  const replaceHeroCapture = (capture: HeroCaptureBundle | null) => {
    setHeroCapture(capture);
    setHeroPreviewUrl((current) => {
      if (current) URL.revokeObjectURL(current);
      return capture ? URL.createObjectURL(capture.hero16x9.blob) : null;
    });
  };

  const replaceCatalogHoverArtifact = (artifact: CatalogHoverVideoArtifact | null) => {
    catalogHoverArtifactRef.current = artifact;
    setCatalogHoverArtifact(artifact);
    setCatalogHoverPreviewUrl((current) => {
      if (current) URL.revokeObjectURL(current);
      return artifact ? URL.createObjectURL(artifact.blob) : null;
    });
  };

  const clearCatalogHoverCapture = (state: CatalogHoverCaptureState = 'waiting_for_preview') => {
    catalogHoverAbort.current?.abort();
    catalogHoverAbort.current = null;
    catalogHoverCapturePromise.current = null;
    replaceCatalogHoverArtifact(null);
    setCatalogHoverProgress(0);
    setCatalogHoverState(state);
  };

  const ensureCatalogHoverVideo = async (): Promise<CatalogHoverVideoArtifact | null> => {
    const runtime = runtimeRef.current;
    if (!runtime || !heroCapture) return null;
    if (catalogHoverQaScenario === 'encoding_unavailable') {
      setCatalogHoverState('unavailable');
      return null;
    }
    const project = runtime.getProjectSnapshot();
    const [visualFingerprint, cameraFingerprint] = await Promise.all([
      createVisualProjectFingerprint(project),
      createHeroCameraFingerprint(project.heroCamera),
    ]);
    const existing = catalogHoverArtifactRef.current;
    if (existing && catalogHoverArtifactMatches(existing, visualFingerprint, cameraFingerprint))
      return existing;
    if (catalogHoverCapturePromise.current) return catalogHoverCapturePromise.current;

    catalogHoverAbort.current?.abort();
    const controller = new AbortController();
    catalogHoverAbort.current = controller;
    setCatalogHoverState('generating');
    setCatalogHoverProgress(0);
    const capture = runtime
      .captureCatalogHoverVideo({
        signal: controller.signal,
        onProgress: setCatalogHoverProgress,
      })
      .then(async (artifact) => {
        const latest = runtime.getProjectSnapshot();
        const [latestVisual, latestCamera] = await Promise.all([
          createVisualProjectFingerprint(latest),
          createHeroCameraFingerprint(latest.heroCamera),
        ]);
        if (!catalogHoverArtifactMatches(artifact, latestVisual, latestCamera)) {
          setCatalogHoverState('stale');
          return null;
        }
        replaceCatalogHoverArtifact(artifact);
        setCatalogHoverProgress(1);
        setCatalogHoverState('ready');
        return artifact;
      })
      .catch((error: unknown) => {
        if (error instanceof DOMException && error.name === 'AbortError') return null;
        setCatalogHoverState(
          error instanceof Error && error.name === 'CatalogHoverUnavailableError'
            ? 'unavailable'
            : 'failed',
        );
        return null;
      })
      .finally(() => {
        if (catalogHoverAbort.current === controller) catalogHoverAbort.current = null;
        if (catalogHoverCapturePromise.current === capture)
          catalogHoverCapturePromise.current = null;
      });
    catalogHoverCapturePromise.current = capture;
    return capture;
  };

  useEffect(() => {
    if (!publishOpen || !heroCapture || heroCaptureState !== 'captured') return;
    void ensureCatalogHoverVideo();
  }, [publishOpen, heroCapture, heroCaptureState]);

  useEffect(() => {
    const artifact = catalogHoverArtifactRef.current;
    const runtime = runtimeRef.current;
    if (!artifact || !runtime) return;
    let cancelled = false;
    void Promise.all([
      createVisualProjectFingerprint(runtime.getProjectSnapshot()),
      createHeroCameraFingerprint(runtime.getProjectSnapshot().heroCamera),
    ]).then(([visualFingerprint, cameraFingerprint]) => {
      if (cancelled || catalogHoverArtifactMatches(artifact, visualFingerprint, cameraFingerprint))
        return;
      clearCatalogHoverCapture('stale');
    });
    return () => {
      cancelled = true;
    };
  }, [state.editorRevision]);

  useEffect(
    () => () => {
      catalogHoverAbort.current?.abort();
      const previewUrl = catalogHoverPreviewUrl;
      if (previewUrl) URL.revokeObjectURL(previewUrl);
    },
    [catalogHoverPreviewUrl],
  );

  const persistPublishWorkspace = async (
    workspace: CreatorPublishWorkspaceV1,
    options: { clearShare?: boolean } = {},
  ) => {
    const queued = publishWorkspaceWriteTail.current.then(async () => {
      const storedWorkspace = await publishWorkspaceRepository
        .load(workspace.projectId)
        .catch(() => null);
      const currentWorkspace =
        storedWorkspace?.projectId === workspace.projectId &&
        storedWorkspace.ownerId === workspace.ownerId
          ? storedWorkspace
          : null;
      const mergedWorkspace: CreatorPublishWorkspaceV1 = {
        ...(currentWorkspace ?? workspace),
        ...workspace,
        capturedHero: workspace.capturedHero ?? currentWorkspace?.capturedHero ?? null,
        share: options.clearShare ? null : (workspace.share ?? currentWorkspace?.share ?? null),
        shareProjectFingerprint: options.clearShare
          ? null
          : (workspace.shareProjectFingerprint ??
            currentWorkspace?.shareProjectFingerprint ??
            null),
        updatedAt: new Date().toISOString(),
      };
      await publishWorkspaceRepository.save(mergedWorkspace);
      setPublishWorkspace(mergedWorkspace);
    });
    publishWorkspaceWriteTail.current = queued.then(
      () => undefined,
      () => undefined,
    );
    await queued;
  };

  const hydratePublishContext = async (
    project: CoasterProject,
    options: { showPanel: boolean; knownCloudRecord?: CloudDraftRecord | null },
  ): Promise<boolean> => {
    const token = ++publishHydrationToken.current;
    const editorRevisionAtStart = state.editorRevision;
    setPublishHydration('loading');
    setPublishBusy(true);
    setPublishMessage(null);
    if (options.showPanel) setPublishProject(null);
    try {
      const cloudDraftsAvailable =
        viewer.kind === 'authenticated' &&
        platform.getCapability('cloud_drafts').availability === 'available';
      const cloudResult =
        options.knownCloudRecord !== undefined
          ? null
          : cloudDraftsAvailable
            ? await platform.loadCloudDraft(project.projectId)
            : null;
      if (cloudResult && !cloudResult.ok && cloudResult.error.code !== 'not_found')
        throw new Error(cloudResult.error.message);
      const hydratedCloud =
        options.knownCloudRecord !== undefined
          ? options.knownCloudRecord
          : cloudResult?.ok
            ? cloudResult.value
            : null;
      const statusResult = hydratedCloud
        ? await platform.getPublishStatus(project.projectId)
        : null;
      if (statusResult && !statusResult.ok && statusResult.error.code !== 'not_found')
        throw new Error(statusResult.error.message);

      let workspace = await publishWorkspaceRepository.load(project.projectId);
      const restoredShare: ReadOnlyShare | null = null;
      let hydrationMessage: string | null = null;
      if (workspace?.share) {
        workspace = { ...workspace, share: null, updatedAt: new Date().toISOString() };
        await publishWorkspaceRepository.save(workspace);
        hydrationMessage = 'Create a new private link when you are ready to share this version.';
      }

      let restoredHero: HeroCaptureBundle | null = null;
      let restoredHeroState: 'none' | 'captured' | 'stale' = 'none';
      if (options.showPanel && workspace?.capturedHero) {
        const [visualFingerprint, cameraFingerprint] = await Promise.all([
          createVisualProjectFingerprint(project),
          createHeroCameraFingerprint(project.heroCamera),
        ]);
        if (
          workspace.capturedHero.visualFingerprint === visualFingerprint &&
          workspace.capturedHero.cameraFingerprint === cameraFingerprint
        ) {
          restoredHero = (await runtimeRef.current?.captureHeroAssets()) ?? null;
          restoredHeroState = restoredHero ? 'captured' : 'none';
        } else {
          restoredHeroState = 'stale';
          hydrationMessage = 'Capture a new Preview Image because the ride changed.';
        }
      }

      if (token !== publishHydrationToken.current) return false;
      cloudRecordRef.current = hydratedCloud;
      setCloudRecord(hydratedCloud);
      setPublishStatus(statusResult?.ok ? statusResult.value : null);
      setPublishWorkspace(workspace);
      setReadOnlyShare(restoredShare);
      replaceHeroCapture(restoredHero);
      setHeroCaptureState(restoredHeroState);
      setCloudConflict(null);
      if (workspace?.lastSyncedProjectFingerprint) {
        const currentFingerprint = await createCloudSyncFingerprint(project);
        lastCloudSyncedEditorRevision.current =
          currentFingerprint === workspace.lastSyncedProjectFingerprint
            ? editorRevisionAtStart
            : null;
      } else {
        lastCloudSyncedEditorRevision.current = hydratedCloud ? editorRevisionAtStart : null;
      }
      if (token !== publishHydrationToken.current) return false;
      setPublishProject(runtimeRef.current?.getProjectSnapshot() ?? project);
      setPublishMessage(hydrationMessage);
      setPublishHydration('ready');
      return true;
    } catch (error) {
      if (token !== publishHydrationToken.current) return false;
      replaceHeroCapture(null);
      setHeroCaptureState('none');
      setPublishProject(runtimeRef.current?.getProjectSnapshot() ?? project);
      setPublishHydration('recoverable_error');
      setPublishMessage(
        `Your local work is safe. ${
          error instanceof Error ? error.message : 'Publishing details could not be restored.'
        }`,
      );
      return false;
    } finally {
      if (token === publishHydrationToken.current) setPublishBusy(false);
    }
  };

  const saveCloudDraft = async (forcedVersion?: number): Promise<CloudDraftRecord | null> => {
    const runtime = runtimeRef.current;
    if (!runtime) return null;
    const draftState = runtime.snapshot();
    if (!draftState.hasSavedDraft || draftState.hasUnsavedChanges) {
      setPublishMessage('Save Draft before using publishing or private links.');
      return null;
    }
    setPublishBusy(true);
    setPublishMessage(null);
    try {
      return await enqueueCloudWrite(async () => {
        const project = runtime.getProjectSnapshot();
        const currentCloud = cloudRecordRef.current;
        const expectedVersion =
          forcedVersion ??
          (currentCloud?.projectId === project.projectId ? currentCloud.version : null);
        const result = await platform.saveCloudDraft(project, { expectedVersion });
        if (!result.ok) {
          if (result.error.code === 'conflict') {
            const current = result.error.details?.currentVersion;
            setCloudConflict({
              projectId: project.projectId,
              currentVersion: typeof current === 'number' ? current : (expectedVersion ?? 0) + 1,
              detectedAt: new Date().toISOString(),
            });
            setPublishMessage(
              'This cloud draft changed in another session. Choose which copy to keep.',
            );
          } else {
            setPublishMessage(`Your local draft is safe. ${result.error.message}`);
          }
          return null;
        }
        cloudRecordRef.current = result.value;
        setCloudRecord(result.value);
        lastCloudSyncedEditorRevision.current = state.editorRevision;
        setCloudConflict(null);
        if (viewer.kind === 'authenticated') {
          const storedWorkspace = publishWorkspaceRepository
            ? await publishWorkspaceRepository.load(project.projectId).catch(() => null)
            : null;
          const baseWorkspace =
            storedWorkspace?.projectId === project.projectId &&
            storedWorkspace.ownerId === viewer.id
              ? storedWorkspace
              : createEmptyPublishWorkspace(viewer.id, project.projectId);
          await persistPublishWorkspace({
            ...baseWorkspace,
            lastCloudVersion: result.value.version,
            lastSyncedProjectFingerprint: await createCloudSyncFingerprint(project),
            updatedAt: new Date().toISOString(),
          });
        }
        setPublishMessage(
          localQaContext ? 'QA draft saved in this browser.' : 'Cloud draft saved.',
        );
        await refreshCloudDrafts();
        return result.value;
      });
    } finally {
      setPublishBusy(false);
    }
  };

  const prepareShareRideSnapshot = async (
    surfaceErrors = false,
  ): Promise<CloudDraftRecord | null> => {
    const runtime = runtimeRef.current;
    if (
      !runtime ||
      !runtime.snapshot().recording.allowed ||
      viewer.kind !== 'authenticated' ||
      shareDraftCapability.availability !== 'available'
    )
      return null;

    const project = runtime.getProjectSnapshot();
    const fingerprint = await createCloudSyncFingerprint(project);
    const prepared = preparedShareRideRef.current;
    if (prepared?.projectId === project.projectId && prepared.fingerprint === fingerprint)
      return prepared.cloud;

    const pending = shareRidePreparationRef.current;
    if (pending?.projectId === project.projectId && pending.fingerprint === fingerprint)
      return pending.promise;

    const promise = enqueueCloudWrite(async () => {
      let currentCloud =
        cloudRecordRef.current?.projectId === project.projectId ? cloudRecordRef.current : null;
      if (!currentCloud) {
        const loaded = await platform.loadCloudDraft(project.projectId);
        if (loaded.ok) currentCloud = loaded.value;
        else if (loaded.error.code !== 'not_found') {
          if (surfaceErrors) setPublishMessage(`Your local draft is safe. ${loaded.error.message}`);
          return null;
        }
      }

      const result = await platform.saveCloudDraft(project, {
        expectedVersion: currentCloud?.version ?? null,
      });
      if (!result.ok) {
        if (result.error.code === 'conflict') {
          const current = result.error.details?.currentVersion;
          setCloudConflict({
            projectId: project.projectId,
            currentVersion:
              typeof current === 'number' ? current : (currentCloud?.version ?? 0) + 1,
            detectedAt: new Date().toISOString(),
          });
        }
        if (surfaceErrors) setPublishMessage(`Your local draft is safe. ${result.error.message}`);
        return null;
      }

      cloudRecordRef.current = result.value;
      setCloudRecord(result.value);
      setCloudConflict(null);
      const storedWorkspace = await publishWorkspaceRepository
        .load(project.projectId)
        .catch(() => null);
      const baseWorkspace =
        storedWorkspace?.projectId === project.projectId && storedWorkspace.ownerId === viewer.id
          ? storedWorkspace
          : createEmptyPublishWorkspace(viewer.id, project.projectId);
      await persistPublishWorkspace({
        ...baseWorkspace,
        lastCloudVersion: result.value.version,
        lastSyncedProjectFingerprint: fingerprint,
        updatedAt: new Date().toISOString(),
      });

      const latestRuntime = runtimeRef.current;
      if (!latestRuntime || !latestRuntime.snapshot().recording.allowed) return null;
      const latestProject = latestRuntime.getProjectSnapshot();
      const latestFingerprint = await createCloudSyncFingerprint(latestProject);
      if (latestProject.projectId !== project.projectId || latestFingerprint !== fingerprint)
        return null;

      preparedShareRideRef.current = {
        projectId: project.projectId,
        fingerprint,
        cloud: result.value,
      };
      lastCloudSyncedEditorRevision.current = latestRuntime.snapshot().editorRevision;
      if (readOnlyShareFingerprintRef.current)
        setShareDraftStale(readOnlyShareFingerprintRef.current !== fingerprint);
      return result.value;
    });
    const preparation = { projectId: project.projectId, fingerprint, promise };
    shareRidePreparationRef.current = preparation;
    setShareRidePreparing(true);
    try {
      return await promise;
    } finally {
      if (shareRidePreparationRef.current === preparation) {
        shareRidePreparationRef.current = null;
        setShareRidePreparing(false);
      }
    }
  };

  const synchronizeShareRideToLatestTest = async (
    surfaceErrors = false,
  ): Promise<ReadOnlyShare | null> => {
    if (shareRideRotationRef.current) return shareRideRotationRef.current;

    const operation = (async () => {
      const runtime = runtimeRef.current;
      if (!runtime?.snapshot().recording.allowed) return null;

      let saved = await prepareShareRideSnapshot(surfaceErrors);
      if (!saved && runtimeRef.current?.snapshot().recording.allowed) {
        await new Promise<void>((resolve) => window.setTimeout(resolve, 0));
        saved = await prepareShareRideSnapshot(surfaceErrors);
      }
      if (!saved) {
        if (surfaceErrors)
          setPublishMessage('We could not prepare this Coaster Ride link. Try again.');
        return null;
      }

      const latestRuntime = runtimeRef.current;
      if (!latestRuntime?.snapshot().recording.allowed) return null;
      const latestProject = latestRuntime.getProjectSnapshot();
      const fingerprint = await createCloudSyncFingerprint(latestProject);
      const testedAt = latestProject.successfulTest?.testedAt ?? null;
      if (!testedAt) return null;
      const currentShare = readOnlyShareRef.current;
      if (currentShare && readOnlyShareFingerprintRef.current === fingerprint) return currentShare;
      if (currentShare && readOnlyShareTestedAtRef.current === testedAt) {
        setShareDraftStale(true);
        return currentShare;
      }

      if (currentShare) {
        const revoked = await platform.revokeReadOnlyShare(currentShare.shareId);
        if (!revoked.ok) {
          if (surfaceErrors) setPublishMessage(revoked.error.message);
          setShareDraftStale(true);
          return null;
        }
        readOnlyShareRef.current = null;
        setReadOnlyShare(null);
        readOnlyShareFingerprintRef.current = null;
        readOnlyShareTestedAtRef.current = null;
      }

      const created = await platform.createReadOnlyShare(saved.projectId, saved.version);
      if (!created.ok) {
        if (surfaceErrors) setPublishMessage(created.error.message);
        return null;
      }

      // The capability-bearing URL remains memory-only. A newly certified
      // revision revokes the prior link before this replacement is minted.
      readOnlyShareRef.current = created.value;
      setReadOnlyShare(created.value);
      readOnlyShareFingerprintRef.current = fingerprint;
      readOnlyShareTestedAtRef.current = testedAt;
      setShareDraftStale(false);
      setPublishMessage(null);
      return created.value;
    })();

    shareRideRotationRef.current = operation;
    setShareRidePreparing(true);
    try {
      return await operation;
    } catch (error) {
      if (surfaceErrors)
        setPublishMessage(
          error instanceof Error
            ? `Your local draft is safe. ${error.message}`
            : 'We could not prepare this Coaster Ride link. Try again.',
        );
      return null;
    } finally {
      if (shareRideRotationRef.current === operation) shareRideRotationRef.current = null;
      setShareRidePreparing(false);
    }
  };

  useEffect(() => {
    if (!shareRideAllowed || visitor) {
      preparedShareRideRef.current = null;
      return;
    }
    const timer = window.setTimeout(() => {
      void synchronizeShareRideToLatestTest(false);
    }, 600);
    return () => window.clearTimeout(timer);
  }, [shareRideAllowed, state.editorRevision, state.fixtureId, visitor]);

  const openCloudDraft = async (projectId: string): Promise<CloudDraftRecord | null> => {
    setPublishBusy(true);
    setLaunchMessage('Loading cloud draft…');
    try {
      const result = await platform.loadCloudDraft(projectId);
      if (!result.ok) {
        setLaunchMessage(result.error.message);
        return null;
      }
      runtimeRef.current?.openEditor();
      await runtimeRef.current?.loadProject(result.value.project, 'editable_cloud');
      await hydratePublishContext(result.value.project, {
        showPanel: false,
        knownCloudRecord: result.value,
      });
      setVisitor(null);
      setDraftsOpen(false);
      setLaunchMessage(null);
      return result.value;
    } finally {
      setPublishBusy(false);
    }
  };

  const deleteCloudDraft = async (draft: CloudDraftSummary) => {
    setPublishBusy(true);
    try {
      const result = await platform.deleteCloudDraft(draft.projectId, draft.version);
      setPublishMessage(result.ok ? 'Cloud draft deleted.' : result.error.message);
      if (result.ok) {
        if (cloudRecord?.projectId === draft.projectId) setCloudRecord(null);
        await refreshCloudDrafts();
      }
    } finally {
      setPublishBusy(false);
    }
  };

  const captureHero = async () => {
    const runtime = runtimeRef.current;
    if (!runtime) return;
    clearCatalogHoverCapture('waiting_for_preview');
    setPublishBusy(true);
    setPublishMessage('Capturing your Preview Image…');
    try {
      const capture = await runtime.captureHeroAssets();
      const project = runtime.getProjectSnapshot();
      const baseWorkspace =
        publishWorkspace?.projectId === project.projectId &&
        publishWorkspace.ownerId === publishWorkspaceOwnerId
          ? publishWorkspace
          : createEmptyPublishWorkspace(publishWorkspaceOwnerId, project.projectId);
      await persistPublishWorkspace({
        ...baseWorkspace,
        capturedHero: {
          visualFingerprint: await createVisualProjectFingerprint(project),
          cameraFingerprint: await createHeroCameraFingerprint(capture.camera),
          capturedAt: new Date().toISOString(),
        },
        updatedAt: new Date().toISOString(),
      });
      replaceHeroCapture(capture);
      setHeroCaptureState('captured');
      setPublishMessage('Preview Image captured.');
    } catch (error) {
      setPublishMessage(
        error instanceof Error ? error.message : 'The Preview Image could not be captured.',
      );
    } finally {
      setPublishBusy(false);
    }
  };

  const performSubmitForReview = async (details: PublishingDetails) => {
    if (!heroCapture) {
      setPublishMessage('Capture a Preview Image before submitting.');
      return;
    }
    runtimeRef.current?.setPublishingDetails(details);
    setPublishProject(runtimeRef.current?.getProjectSnapshot() ?? null);
    if (!(await saveCurrentDraft())) return;
    const saved = await saveCloudDraft();
    if (!saved) return;
    setPublishBusy(true);
    try {
      setPublishMessage('Preparing the Buncha Games preview…');
      const hoverArtifact = await ensureCatalogHoverVideo();
      setPublishMessage('Uploading the Preview Image…');
      const uploaded = await platform.uploadHeroAssets(saved.projectId, {
        hero16x9: heroCapture.hero16x9.blob,
        openGraph: heroCapture.openGraph.blob,
      });
      if (!uploaded.ok) {
        runtimeRef.current?.playUiFeedback('system_failure');
        setPublishMessage(uploaded.error.message);
        return;
      }
      let catalogHoverUpload = null;
      let usedStillFallback = hoverArtifact === null;
      if (
        hoverArtifact &&
        catalogHoverQaScenario !== 'upload_failure' &&
        platform.getCapability('catalog_hover_upload').availability === 'available'
      ) {
        setPublishMessage('Uploading the 8-second motion preview…');
        const uploadedHover = await platform.uploadCatalogHoverVideo(
          saved.projectId,
          {
            blob: hoverArtifact.blob,
            width: hoverArtifact.width as 640 | 480,
            height: hoverArtifact.height as 360 | 270,
            durationSeconds: hoverArtifact.durationSeconds,
            visualFingerprint: hoverArtifact.visualFingerprint,
          },
          `${saved.projectId}:${saved.version}:${hoverArtifact.visualFingerprint}`,
        );
        if (uploadedHover.ok) catalogHoverUpload = uploadedHover.value;
        else usedStillFallback = true;
      } else if (hoverArtifact) {
        usedStillFallback = true;
      }
      setPublishMessage('Submitting this version to Buncha Games…');
      const publishOperation = `${saved.projectId}:${saved.version}`;
      if (publishIdempotency.current?.operation !== publishOperation)
        publishIdempotency.current = { operation: publishOperation, key: crypto.randomUUID() };
      const result = await platform.requestPublish({
        projectId: saved.projectId,
        expectedVersion: saved.version,
        idempotencyKey: publishIdempotency.current.key,
        heroUpload: uploaded.value,
        catalogHoverUpload,
      });
      if (!result.ok) {
        runtimeRef.current?.playUiFeedback('system_failure');
        setPublishMessage(result.error.message);
        return;
      }
      setPublishStatus(result.value);
      runtimeRef.current?.playUiFeedback('publish_success');
      setCloudRecord({
        ...saved,
        state: result.value.state,
        pendingRevisionId: result.value.pendingRevisionId,
        currentPublicRevisionId: result.value.currentPublicRevisionId,
        publicCoasterId: result.value.publicCoasterId,
        publicSlug: result.value.publicSlug,
      });
      setPublishMessage(
        usedStillFallback
          ? 'Submitted with the Preview Image fallback. Your current public revision stays live while this update is reviewed.'
          : 'Submitted with its motion preview. Your current public revision stays live while this update is reviewed.',
      );
    } finally {
      setPublishBusy(false);
    }
  };

  const submitForReview = async (details: PublishingDetails) => {
    if (publishSubmissionPromise.current) return publishSubmissionPromise.current;
    setPublishSubmitting(true);
    const submission = performSubmitForReview(details).finally(() => {
      setPublishSubmitting(false);
      if (publishSubmissionPromise.current === submission) publishSubmissionPromise.current = null;
    });
    publishSubmissionPromise.current = submission;
    return submission;
  };

  const createReadOnlyShare = async () => {
    const runtime = runtimeRef.current;
    if (!runtime) return;
    if (!runtime.snapshot().recording.allowed) {
      setPublishMessage(POST_TEST_ACTIONS_REASON);
      return;
    }
    setPublishBusy(true);
    try {
      await synchronizeShareRideToLatestTest(true);
    } finally {
      setPublishBusy(false);
    }
  };

  const revokeReadOnlyShare = async () => {
    if (!readOnlyShare) return;
    setPublishBusy(true);
    setPublishMessage('Revoking Coaster Ride link…');
    try {
      const result = await platform.revokeReadOnlyShare(readOnlyShare.shareId);
      if (result.ok) {
        readOnlyShareRef.current = null;
        setReadOnlyShare(null);
        readOnlyShareFingerprintRef.current = null;
        readOnlyShareTestedAtRef.current = null;
        setShareDraftStale(false);
        if (publishWorkspace) {
          await persistPublishWorkspace(
            {
              ...publishWorkspace,
              share: null,
              shareProjectFingerprint: null,
              updatedAt: new Date().toISOString(),
            },
            { clearShare: true },
          );
        }
        setPublishMessage('Coaster Ride link revoked.');
      } else setPublishMessage(result.error.message);
    } catch (error) {
      setPublishMessage(
        error instanceof Error
          ? `The Coaster Ride link could not be revoked. ${error.message}`
          : 'The Coaster Ride link could not be revoked. Try again.',
      );
    } finally {
      setPublishBusy(false);
    }
  };

  const loadCloudConflict = async () => {
    if (!cloudConflict) return;
    setPublishBusy(true);
    try {
      const result = await platform.loadCloudDraft(cloudConflict.projectId);
      if (!result.ok) {
        setPublishMessage(`Cloud copy could not be loaded: ${result.error.message}`);
        return;
      }
      runtimeRef.current?.openEditor();
      await runtimeRef.current?.loadProject(result.value.project, 'editable_cloud');
      const hydrated = await hydratePublishContext(result.value.project, {
        showPanel: true,
        knownCloudRecord: result.value,
      });
      if (!hydrated) return;
      setCloudConflict(null);
      setPublishMessage(
        `Loaded ${result.value.project.metadata.title} for ${result.value.project.metadata.creator.displayName} · cloud version ${result.value.version}.`,
      );
    } finally {
      setPublishBusy(false);
    }
  };

  const keepLocalConflict = async () => {
    if (!cloudConflict) return;
    await saveCloudDraft(cloudConflict.currentVersion);
  };

  const remixVisitorCoaster = async () => {
    if (!visitor) return;
    setPublishBusy(true);
    setLaunchMessage('Creating an independent draft…');
    try {
      const result = await platform.remixCoaster(
        visitor.kind === 'public'
          ? { publicCoasterId: visitor.record.publicCoasterId }
          : {
              share: {
                shareId: visitor.record.shareId,
                secret: new URLSearchParams(window.location.hash.slice(1)).get('cap') ?? '',
              },
            },
      );
      if (!result.ok) {
        setLaunchMessage(result.error.message);
        return;
      }
      await runtimeRef.current?.loadProject(result.value.draft.project, 'editable_remix');
      setActiveRemixSource(activePublicCard);
      setCloudRecord(result.value.draft);
      setPublishStatus(null);
      setVisitor(null);
      setActivePublicCard(null);
      referralRide.current = null;
      setLaunchMessage(null);
      history.replaceState(null, '', editorResetPath);
    } finally {
      setPublishBusy(false);
    }
  };

  const approveLocalQaRevision = async () => {
    if (!cloudRecord || !isLocalPhase4QaPlatformAdapter(platform)) return;
    const result = await platform.moderatePending(cloudRecord.projectId, { kind: 'approve' });
    if (result.ok) {
      setPublishStatus(result.value);
      setCloudRecord({
        ...cloudRecord,
        state: result.value.state,
        pendingRevisionId: result.value.pendingRevisionId,
        currentPublicRevisionId: result.value.currentPublicRevisionId,
        publicCoasterId: result.value.publicCoasterId,
        publicSlug: result.value.publicSlug,
      });
      setPublishMessage('Local QA moderation approved this revision.');
      await refreshCloudDrafts();
    } else setPublishMessage(result.error.message);
  };

  const statusTone = useMemo(() => {
    if (state.status.toLowerCase().includes('invalid')) return 'danger';
    if (
      state.status.toLowerCase().includes('unavailable') ||
      state.status.toLowerCase().includes('saving is off') ||
      state.status.toLowerCase().includes("couldn't save") ||
      state.status.toLowerCase().includes('stalled')
    )
      return 'warning';
    return 'neutral';
  }, [state.status]);
  const activeCloudRecord =
    cloudRecord && cloudRecord.projectId === publishProject?.projectId ? cloudRecord : null;
  const activePublishStatus =
    publishStatus && publishStatus.projectId === publishProject?.projectId ? publishStatus : null;
  const activeStatus = activePublishStatus
    ? ownerProjectStatusLabel(activePublishStatus.state)
    : activeCloudRecord
      ? ownerProjectStatusLabel(activeCloudRecord.state)
      : 'local_only';
  const activePublicSlug = activePublishStatus?.publicSlug ?? activeCloudRecord?.publicSlug ?? null;
  const activePublicUrl = activePublicSlug
    ? buildPublicCoasterUrl({
        publicSlug: activePublicSlug,
        publicBaseUrl,
        localQa: localQaContext,
      })
    : null;
  const activeShareUrl = readOnlyShare
    ? readOnlyShare.url.startsWith('http')
      ? readOnlyShare.url
      : buildReadOnlyShareUrl({
          sharePath: readOnlyShare.url,
          publicBaseUrl,
          localQa: localQaContext,
        })
    : null;
  useEffect(() => {
    const runtime = runtimeRef.current;
    if (!runtime) return;
    const project = runtime.getProjectSnapshot();
    const publicRecord = visitor?.kind === 'public' ? visitor.record : null;
    const visitorPublicUrl = publicRecord
      ? buildPublicCoasterUrl({
          publicSlug: publicRecord.publicSlug,
          publicBaseUrl,
          localQa: localQaContext,
        })
      : null;
    runtime.setMediaShareContext({
      creatorDisplayName:
        publicRecord?.creator.displayName ??
        (visitor?.kind === 'unlisted' || visitor?.kind === 'private'
          ? visitor.record.project.metadata.creator.displayName
          : project.metadata.creator.displayName),
      publicCoasterId:
        publicRecord?.publicCoasterId ??
        activePublishStatus?.publicCoasterId ??
        activeCloudRecord?.publicCoasterId ??
        null,
      publicUrl: visitorPublicUrl ?? activePublicUrl,
    });
  }, [
    activeCloudRecord?.publicCoasterId,
    activePublicUrl,
    activePublishStatus?.publicCoasterId,
    localQaContext,
    publicBaseUrl,
    state.fixtureId,
    visitor,
  ]);
  const lastOpenedDraft = state.lastOpenedProjectId
    ? (state.drafts.find(
        (draft) => draft.projectId === state.lastOpenedProjectId && !draft.corrupt,
      ) ?? null)
    : null;
  const draftPendingDeletion = confirmDeleteProjectId
    ? (state.drafts.find((draft) => draft.projectId === confirmDeleteProjectId) ?? null)
    : null;

  const clearPublishContext = (options: { preserveCatalogHover?: boolean } = {}) => {
    publishHydrationToken.current += 1;
    setPublishOpen(false);
    setShareDraftOpen(false);
    setShareDraftStale(false);
    setPublishHydration('idle');
    setPublishBusy(false);
    setPublishProject(null);
    setPublishMessage(null);
    setCloudConflict(null);
    cloudRecordRef.current = null;
    setCloudRecord(null);
    setPublishStatus(null);
    setPublishWorkspace(null);
    readOnlyShareRef.current = null;
    setReadOnlyShare(null);
    readOnlyShareFingerprintRef.current = null;
    readOnlyShareTestedAtRef.current = null;
    replaceHeroCapture(null);
    setHeroCaptureState('none');
    if (!options.preserveCatalogHover || !catalogHoverArtifactRef.current)
      clearCatalogHoverCapture('waiting_for_preview');
    lastCloudSyncedEditorRevision.current = null;
  };

  const returnToMainMenu = () => {
    clearPublishContext();
    setConfirmExit(false);
    setConfirmPublishSave(false);
    pendingProjectNameRef.current = null;
    setRenameValue(null);
    setActiveRemixSource(null);
    runtimeRef.current?.returnToMenu();
  };

  const returnVisitorToMainMenu = () => {
    setVisitor(null);
    setActivePublicCard(null);
    referralRide.current = null;
    setPendingWorldLaunch(null);
    setPendingPrivateDraftRevision(null);
    worldLaunchSession.clearDirectContext();
    setLaunchMessage(null);
    launchAttempted.current = false;
    history.replaceState(null, '', editorResetPath);
    runtimeRef.current?.returnToMenu();
  };

  const continueToExitDestination = () => {
    const destination = exitDestination;
    setConfirmExit(false);
    setExitDestination('main_menu');
    if (destination === 'creator_guide') runtimeRef.current?.restartCreatorGuide();
    else if (destination === 'finish_challenge') runtimeRef.current?.startFinishLesson();
    else returnToMainMenu();
  };

  const requestExitDestination = (destination: ExitDestination) => {
    setHelpOpen(false);
    if (state.hasUnsavedChanges || hasPendingNameChange) {
      setExitDestination(destination);
      setConfirmExit(true);
      return;
    }
    if (destination === 'creator_guide') runtimeRef.current?.restartCreatorGuide();
    else if (destination === 'finish_challenge') runtimeRef.current?.startFinishLesson();
    else returnToMainMenu();
  };

  const commitPendingProjectName = (): boolean => {
    const pendingName = pendingProjectNameRef.current;
    if (pendingName === null) return true;
    const runtime = runtimeRef.current;
    if (!runtime) return false;
    if (pendingName === runtime.snapshot().fixtureTitle) {
      pendingProjectNameRef.current = null;
      setRenameValue(null);
      return true;
    }
    const renamed = runtime.renameProject(pendingName);
    if (renamed) {
      pendingProjectNameRef.current = null;
      setRenameValue(null);
    } else projectNameRef.current?.focus();
    return renamed;
  };

  const saveCurrentDraft = async (): Promise<boolean> => {
    if (!commitPendingProjectName()) return false;
    const runtime = runtimeRef.current;
    if (!runtime) return false;
    const saved = await runtime.saveNow();
    if (!saved) return false;
    const projectId = runtime.getProjectSnapshot().projectId;
    try {
      const capture = await runtime.captureHeroAssets();
      const preview = await createDraftPreview(capture.hero16x9.blob);
      if (preview) {
        await draftPreviewRepository.save(projectId, preview);
        const nextUrl = URL.createObjectURL(preview);
        setDraftPreviewUrls((current) => {
          const old = current[projectId];
          if (old) URL.revokeObjectURL(old);
          const next = { ...current, [projectId]: nextUrl };
          draftPreviewUrlsRef.current = next;
          return next;
        });
      }
    } catch {
      // A draft remains successfully saved when its optional preview fails.
    }
    return true;
  };

  const openPublishPanel = () => {
    if (!runtimeRef.current?.snapshot().recording.allowed) return;
    const project = runtimeRef.current?.getProjectSnapshot();
    if (!project) return;
    clearPublishContext({ preserveCatalogHover: true });
    setPublishSessionId((value) => value + 1);
    setPublishOpen(true);
    setPublishProject(null);
    setPublishHydration('loading');
    setPublishMessage(null);
    void hydratePublishContext(project, { showPanel: true });
  };

  const openShareRideDialog = () => {
    if (!shareRideAllowed) return;
    const project = runtimeRef.current?.getProjectSnapshot();
    if (!project) return;
    if (readOnlyShare && publishProject?.projectId === project.projectId) {
      setPublishMessage(null);
      setShareDraftOpen(true);
      return;
    }
    setPublishOpen(false);
    setShareDraftOpen(true);
    setPublishProject(project);
    setPublishHydration('loading');
    setPublishMessage(null);
    void createReadOnlyShare().finally(() => setPublishHydration('ready'));
  };

  const openOwnerFlowFromRide = (flow: 'publish' | 'share_draft') => {
    runtimeRef.current?.returnToEditor();
    window.setTimeout(() => {
      if (flow === 'publish') openPublishPanel();
      else openShareRideDialog();
    }, 0);
  };

  const openRecordRideChoice = (camera: 'first-person' | 'third-person') => {
    setTopbarMenuOpen(false);
    setRecordRideCamera(camera);
  };

  const beginRecordRideWithoutReaction = () => {
    if (!recordRideCamera) return;
    const camera = recordRideCamera;
    setRecordRideCamera(null);
    runtimeRef.current?.startRecordRide(camera);
  };

  const beginRecordRideWithReaction = () => {
    if (!recordRideCamera || !reactionCamAvailable) return;
    const camera = recordRideCamera;
    setRecordRideCamera(null);
    runtimeRef.current?.openReactionCamSetup(camera);
  };

  return (
    <main
      class={`app-shell mode-${state.mode} ride-phase-${state.ride.phase} selection-target-${state.selectionTarget} ${state.mode === 'menu' && !visitor ? 'creator-main-menu' : ''} ${state.onboarding.creator.active || state.onboarding.finishLesson.active ? 'onboarding-gated' : ''}`}
      onPointerDownCapture={() => {
        if (state.settings.audioState === 'locked') runtimeRef.current?.unlockAudio();
      }}
      onClickCapture={(event) => {
        const target = event.target;
        if (!(target instanceof Element)) return;
        const button = target.closest<HTMLButtonElement>('button');
        if (!button || button.disabled) return;
        const cue = button.dataset.audioCue;
        if (cue === 'track-place' || cue === 'none') return;
        const buildSelection =
          cue === 'build-select' ||
          Boolean(button.closest('.basic-build-composer, .modular-build-options'));
        runtimeRef.current?.playUiClick(buildSelection ? 'build_select' : 'general');
      }}
    >
      <header class="topbar">
        <div class="brand-navigation">
          <div class="brand-lockup">
            <BrandLogo compact={inEditor} />
            <h1 class="sr-only">Buncha Coasters</h1>
          </div>
          {inEditor && !visitor && (
            <nav class="platform-actions" aria-label="Game navigation">
              <Button
                variant="tertiary"
                size="compact"
                icon={ArrowLeft}
                type="button"
                onClick={() => {
                  requestExitDestination('main_menu');
                }}
              >
                {UI_COPY.navigation.backToMenu}
              </Button>
              <IconButton
                label={UI_COPY.navigation.help}
                icon={CircleHelp}
                size="compact"
                type="button"
                onClick={() => setHelpOpen(true)}
              />
              <span class="desktop-project-utilities" role="group" aria-label="Project utilities">
                <IconButton
                  class="desktop-sound-toggle"
                  label={state.settings.muted ? 'Turn Sound On' : 'Turn Sound Off'}
                  icon={state.settings.muted ? VolumeX : Volume2}
                  size="compact"
                  type="button"
                  data-audio-cue="none"
                  aria-pressed={state.settings.muted}
                  onClick={() => runtimeRef.current?.setMuted(!state.settings.muted)}
                />
                {debugControlsAvailable && (
                  <IconButton
                    class="desktop-debug-toggle"
                    label={state.debugVisible ? 'Hide Debug Panel' : 'Show Debug Panel'}
                    icon={Bug}
                    size="compact"
                    type="button"
                    aria-pressed={state.debugVisible}
                    onClick={() => runtimeRef.current?.setDebugVisible(!state.debugVisible)}
                  />
                )}
              </span>
            </nav>
          )}
        </div>
        <div class="topbar-actions">
          {inEditor && !visitor ? (
            <>
              <div class="project-identity">
                <label class="project-name-field">
                  <span class="sr-only">Coaster name</span>
                  <input
                    ref={projectNameRef}
                    value={renameValue ?? state.fixtureTitle}
                    maxLength={80}
                    onFocus={() => {
                      if (pendingProjectNameRef.current === null) {
                        pendingProjectNameRef.current = state.fixtureTitle;
                        setRenameValue(state.fixtureTitle);
                      }
                    }}
                    onInput={(event) => {
                      pendingProjectNameRef.current = event.currentTarget.value;
                      setRenameValue(event.currentTarget.value);
                    }}
                    onBlur={() => {
                      if (pendingProjectNameRef.current?.trim().length === 0) return;
                      commitPendingProjectName();
                    }}
                    onKeyDown={(event) => {
                      if (event.key === 'Enter') event.currentTarget.blur();
                    }}
                  />
                </label>
                {activeRemixSource && (
                  <span class="remix-source-badge">
                    Remix of {activeRemixSource.title} by {activeRemixSource.creator.displayName}
                  </span>
                )}
                <StatusPill
                  tone={
                    state.saveState === 'error'
                      ? 'danger'
                      : state.saveState === 'clean'
                        ? 'ready'
                        : 'warning'
                  }
                >
                  <span class={`save-state save-${state.saveState}`} data-testid="save-state">
                    {state.saveState === 'saving'
                      ? 'Saving Draft…'
                      : state.saveState === 'error'
                        ? 'Save Failed'
                        : state.saveState === 'editing'
                          ? state.hasSavedDraft || hasPendingNameChange
                            ? 'Unsaved'
                            : 'Unsaved'
                          : 'Draft Saved'}
                  </span>
                </StatusPill>
              </div>
              <Button
                class="topbar-save"
                icon={Save}
                size="compact"
                type="button"
                onPointerDown={() => {
                  // Commit the focused name before blur and click can race during
                  // the same pointer gesture. The click remains responsible for
                  // starting persistence and its optional preview capture.
                  commitPendingProjectName();
                }}
                onClick={() => {
                  setCoordinateRecoveryEpoch((value) => value + 1);
                  void saveCurrentDraft().then((saved) => {
                    if (!saved) projectNameRef.current?.focus();
                  });
                }}
              >
                {UI_COPY.editor.saveDraft}
              </Button>
              <div class="project-history-actions" role="group" aria-label="Project actions">
                {state.media.available && (
                  <IconButton
                    class="topbar-media-action"
                    label={UI_COPY.media.create}
                    icon={Film}
                    size="compact"
                    type="button"
                    onClick={() => void runtimeRef.current?.openMediaStudio()}
                  />
                )}
                <span class="topbar-pretest-actions">
                  <Button
                    class="topbar-test"
                    data-onboarding-target="test-ride"
                    variant="secondary"
                    size="compact"
                    type="button"
                    disabled={!state.testAllowed}
                    onClick={() => runtimeRef.current?.startTestRide('first-person')}
                  >
                    {UI_COPY.editor.testRide}
                  </Button>
                </span>
                <span class="topbar-posttest-cluster">
                  <Button
                    class="topbar-share-draft"
                    icon={Link2}
                    size="compact"
                    type="button"
                    disabled={!shareRideAllowed}
                    title={
                      !state.recording.allowed
                        ? POST_TEST_ACTIONS_REASON
                        : !shareDraftAvailable
                          ? shareDraftCapability.reason
                          : undefined
                    }
                    aria-describedby={
                      !state.recording.allowed ? 'topbar-post-test-reason' : undefined
                    }
                    onClick={openShareRideDialog}
                    data-testid="open-share-ride"
                  >
                    {UI_COPY.editor.shareRide}
                  </Button>
                  <Button
                    class="topbar-record"
                    variant="primary"
                    size="compact"
                    type="button"
                    disabled={!state.recording.allowed}
                    title={state.recording.disabledReason ?? undefined}
                    aria-describedby={
                      !state.recording.allowed ? 'topbar-post-test-reason' : undefined
                    }
                    onClick={() => openRecordRideChoice('first-person')}
                  >
                    {UI_COPY.editor.recordRide}
                  </Button>
                  <Button
                    class="topbar-publish"
                    variant="primary"
                    size="compact"
                    type="button"
                    disabled={!state.recording.allowed}
                    title={!state.recording.allowed ? POST_TEST_ACTIONS_REASON : undefined}
                    aria-describedby={
                      !state.recording.allowed ? 'topbar-post-test-reason' : undefined
                    }
                    onClick={() => {
                      if (!state.hasSavedDraft || state.hasUnsavedChanges || hasPendingNameChange)
                        setConfirmPublishSave(true);
                      else openPublishPanel();
                    }}
                    data-testid="open-publish"
                  >
                    Publish to Buncha Games
                  </Button>
                  {!state.recording.allowed && (
                    <span id="topbar-post-test-reason" class="topbar-record-reason" role="status">
                      {POST_TEST_ACTIONS_REASON}
                    </span>
                  )}
                </span>
                <span class="topbar-overflow">
                  <IconButton
                    label="More project actions"
                    icon={MoreHorizontal}
                    size="compact"
                    type="button"
                    aria-expanded={topbarMenuOpen}
                    onClick={() => setTopbarMenuOpen((open) => !open)}
                  />
                  {topbarMenuOpen && (
                    <div class="bc-popover-menu" role="menu">
                      <button
                        type="button"
                        role="menuitem"
                        disabled={!state.testAllowed}
                        onClick={() => {
                          setTopbarMenuOpen(false);
                          runtimeRef.current?.startTestRide('first-person');
                        }}
                      >
                        Test Ride
                      </button>
                      <button
                        class="compact-share-draft-action"
                        type="button"
                        role="menuitem"
                        disabled={!shareRideAllowed}
                        title={
                          !state.recording.allowed
                            ? POST_TEST_ACTIONS_REASON
                            : !shareDraftAvailable
                              ? shareDraftCapability.reason
                              : undefined
                        }
                        onClick={() => {
                          setTopbarMenuOpen(false);
                          openShareRideDialog();
                        }}
                      >
                        <Link2 aria-hidden="true" size={17} />
                        Share Ride
                      </button>
                      {!state.recording.allowed && (
                        <small id="compact-post-test-reason" class="compact-record-reason">
                          {POST_TEST_ACTIONS_REASON}
                        </small>
                      )}
                      <button
                        type="button"
                        role="menuitem"
                        disabled={!state.recording.allowed}
                        title={state.recording.disabledReason ?? undefined}
                        aria-describedby={
                          !state.recording.allowed ? 'compact-post-test-reason' : undefined
                        }
                        onClick={() => {
                          setTopbarMenuOpen(false);
                          openRecordRideChoice('first-person');
                        }}
                      >
                        Record Ride
                      </button>
                      <button
                        class="compact-publish-action"
                        type="button"
                        role="menuitem"
                        disabled={!state.recording.allowed}
                        title={!state.recording.allowed ? POST_TEST_ACTIONS_REASON : undefined}
                        aria-describedby={
                          !state.recording.allowed ? 'compact-post-test-reason' : undefined
                        }
                        onClick={() => {
                          setTopbarMenuOpen(false);
                          if (
                            !state.hasSavedDraft ||
                            state.hasUnsavedChanges ||
                            hasPendingNameChange
                          )
                            setConfirmPublishSave(true);
                          else openPublishPanel();
                        }}
                      >
                        <Compass aria-hidden="true" size={17} />
                        Publish to Buncha Games
                      </button>
                      <button
                        type="button"
                        role="menuitem"
                        onClick={() => {
                          setTopbarMenuOpen(false);
                          setHelpOpen(true);
                        }}
                      >
                        <CircleHelp aria-hidden="true" size={17} />
                        Help &amp; Settings
                      </button>
                      <button
                        type="button"
                        role="menuitem"
                        data-audio-cue="none"
                        onClick={() => runtimeRef.current?.setMuted(!state.settings.muted)}
                      >
                        {state.settings.muted ? (
                          <Volume2 aria-hidden="true" size={17} />
                        ) : (
                          <VolumeX aria-hidden="true" size={17} />
                        )}
                        {state.settings.muted ? 'Turn Sound On' : 'Mute Sound'}
                      </button>
                      {debugControlsAvailable && (
                        <button
                          type="button"
                          role="menuitem"
                          onClick={() => {
                            setTopbarMenuOpen(false);
                            runtimeRef.current?.setDebugVisible(!state.debugVisible);
                          }}
                        >
                          {state.debugVisible ? 'Hide Debug Panel' : 'Show Debug Panel'}
                        </button>
                      )}
                    </div>
                  )}
                </span>
              </div>
            </>
          ) : (
            <span class="fixture-name">
              <strong>{visitor ? 'Ride a Coaster' : 'Build a Coaster'}</strong>
            </span>
          )}
          {state.debugVisible && <span class="debug-badge">Debug Build</span>}
        </div>
      </header>

      <section class="world-shell" aria-label="Coaster design and ride viewport">
        <canvas ref={canvasRef} id="coaster-canvas" data-testid="coaster-canvas" />
        {state.onboarding.creator.step === 'station' && (
          <div
            class="tutorial-station-drop-zone"
            data-onboarding-target="station-drop-zone"
            aria-hidden="true"
          >
            <span>Place station here</span>
          </div>
        )}
        <div class="canvas-vignette" aria-hidden="true" />
        {state.ride.photoFlash && (
          <div
            class={`ride-photo-flash ${state.settings.reducedMotion ? 'reduced' : ''}`}
            aria-label="On-ride photo taken"
            role="status"
          />
        )}

        {state.mode === 'menu' && !worldLaunchPending && (
          <section
            class={`start-screen social-hub view-${hubView}`}
            data-testid="start-screen"
            aria-labelledby="welcome-title"
          >
            <BrandLogo class="start-screen-brand" />
            <nav class="hub-navigation" aria-label="Coaster hub">
              <button
                type="button"
                aria-current={hubView !== 'my-coasters'}
                onClick={() => setHubView('home')}
              >
                Build
              </button>
              <button
                type="button"
                aria-current={hubView === 'my-coasters'}
                onClick={() => setHubView('my-coasters')}
              >
                My Coasters
              </button>
            </nav>
            {hubView === 'home' && (
              <div class="start-copy">
                <h2 id="welcome-title">What will you build?</h2>
                {state.lastOpenedProjectId && lastOpenedDraft && (
                  <button
                    data-testid="continue-draft"
                    data-audio-cue="none"
                    class="primary-action continue-action"
                    type="button"
                    onClick={() => void runtimeRef.current?.openDraft(state.lastOpenedProjectId!)}
                  >
                    Continue {lastOpenedDraft.title}
                  </button>
                )}
              </div>
            )}
            {hubView === 'home' && (
              <div class="template-grid hub-section-grid" aria-label="Build options">
                <button
                  data-testid="start-new-coaster"
                  data-audio-cue="none"
                  class="template-card"
                  type="button"
                  onClick={() => runtimeRef.current?.startTemplate('blank')}
                >
                  <PencilLine aria-hidden="true" size={26} />
                  <strong>Start a New Coaster</strong>
                </button>
                <button
                  data-testid="my-drafts-card"
                  class="template-card"
                  type="button"
                  onClick={() => setDraftsOpen(!draftsOpen)}
                >
                  <FolderOpen aria-hidden="true" size={26} />
                  <strong>My Drafts ({state.drafts.length})</strong>
                </button>
                <button
                  data-testid="browse-remix-card"
                  class="template-card"
                  type="button"
                  onClick={() => {
                    if (worldHubAvailable) worldLaunchSession.openContentHub();
                    else setHubView('browse');
                  }}
                >
                  <Compass aria-hidden="true" size={26} />
                  <strong>{worldHubAvailable ? 'World Hub' : 'Recent Coasters'}</strong>
                </button>
              </div>
            )}
            {hubView === 'home' && draftsOpen && (
              <section class="draft-library" data-testid="draft-library" aria-label="My Drafts">
                <div class="drawer-heading">
                  <strong>My Drafts</strong>
                  <CloseButton
                    label="Close drafts"
                    size="compact"
                    type="button"
                    onClick={() => setDraftsOpen(false)}
                  />
                </div>
                {state.drafts.length ? (
                  <div class="draft-list">
                    {state.drafts.map((draft) => (
                      <article class={`draft-card ${draft.corrupt ? 'is-corrupt' : ''}`}>
                        <div class="draft-preview">
                          {draftPreviewUrls[draft.projectId] ? (
                            <img src={draftPreviewUrls[draft.projectId]} alt="" />
                          ) : (
                            <span aria-hidden="true">
                              <i />
                            </span>
                          )}
                        </div>
                        <div>
                          <strong>{draft.title}</strong>
                          <small>
                            {draft.corrupt
                              ? draft.errorMessage
                              : new Date(draft.savedAt).toLocaleString()}
                          </small>
                        </div>
                        <div>
                          <button
                            type="button"
                            disabled={draft.corrupt}
                            onClick={() => void runtimeRef.current?.openDraft(draft.projectId)}
                          >
                            Open
                          </button>
                          <button
                            type="button"
                            disabled={draft.corrupt}
                            onClick={() => void runtimeRef.current?.duplicateDraft(draft.projectId)}
                          >
                            Duplicate
                          </button>
                          <button
                            class="danger-action"
                            type="button"
                            onClick={() => setConfirmDeleteProjectId(draft.projectId)}
                          >
                            Delete
                          </button>
                        </div>
                      </article>
                    ))}
                  </div>
                ) : (
                  <p>No saved drafts yet.</p>
                )}
                {viewer.kind === 'authenticated' && (
                  <div class="cloud-draft-section">
                    <div class="drawer-heading">
                      <div>
                        <strong>{localQaContext ? 'Local QA drafts' : 'Buncha Cloud'}</strong>
                        <small>{viewer.displayName}</small>
                      </div>
                      <button type="button" onClick={() => void refreshCloudDrafts()}>
                        Refresh
                      </button>
                    </div>
                    {cloudDrafts.length ? (
                      <div class="draft-list" data-testid="cloud-draft-list">
                        {cloudDrafts.map((draft) => (
                          <article class="draft-card">
                            <div>
                              <strong>{draft.title}</strong>
                              <small>
                                {ownerProjectStatusLabel(draft.state).replaceAll('_', ' ')} ·
                                version {draft.version}
                              </small>
                            </div>
                            <div>
                              <button
                                type="button"
                                disabled={publishBusy}
                                onClick={() => void openCloudDraft(draft.projectId)}
                              >
                                Open
                              </button>
                              {draft.publicSlug && (
                                <a
                                  class="secondary-action"
                                  href={buildPublicCoasterUrl({
                                    publicSlug: draft.publicSlug,
                                    publicBaseUrl,
                                    localQa: localQaContext,
                                  })}
                                  target="_blank"
                                  rel="noreferrer"
                                >
                                  View public
                                </a>
                              )}
                              <button
                                type="button"
                                class="danger-action"
                                disabled={publishBusy}
                                onClick={() => {
                                  if (
                                    window.confirm(
                                      `Delete the cloud draft “${draft.title}”? Your local copy is not deleted.`,
                                    )
                                  )
                                    void deleteCloudDraft(draft);
                                }}
                              >
                                Delete cloud
                              </button>
                            </div>
                          </article>
                        ))}
                      </div>
                    ) : (
                      <p>
                        No {localQaContext ? 'QA' : 'cloud'} drafts yet. Open a coaster and choose
                        Publish to Buncha Games to save one.
                      </p>
                    )}
                  </div>
                )}
              </section>
            )}
            {hubView === 'browse' && (
              <BrowsePanel
                page={catalogPage}
                loading={catalogLoading}
                error={catalogError}
                collection={browseCollection}
                remixableOnly={remixableOnly}
                creatorLabel={catalogCreator?.label ?? null}
                pendingIds={socialPendingIds}
                message={catalogMessage}
                onCollection={(collection) => {
                  setCatalogPage(null);
                  setBrowseCollection(collection);
                  if (collection !== 'favorites') setCatalogSort(collection);
                }}
                onRemixable={(enabled) => {
                  setCatalogPage(null);
                  setRemixableOnly(enabled);
                }}
                onClearCreator={() => {
                  setCatalogPage(null);
                  setCatalogCreator(null);
                }}
                onRetry={() => void loadCatalog(false)}
                onLoadMore={() => void loadCatalog(true)}
                onOpen={(card) => void openPublicCard(card)}
                onRemix={(card) => void remixPublicCard(card)}
                onBoost={(card) => void mutateSocial(card, 'boost')}
                onFavorite={(card) => void mutateSocial(card, 'favorite')}
                onCreator={(id, label) => {
                  setCatalogPage(null);
                  setCatalogCreator({ id, label });
                }}
                onShare={(card) => void copyReferralLink(card)}
                onClose={() => setHubView('home')}
              />
            )}
            {hubView === 'my-coasters' && (
              <MyCoastersPanel
                viewer={viewer}
                published={myCoasters}
                drafts={state.drafts}
                draftPreviewUrls={draftPreviewUrls}
                tab={myCoastersTab}
                loading={myCoastersLoading}
                error={myCoastersError}
                onTab={setMyCoastersTab}
                onOpenDraft={(projectId) => void runtimeRef.current?.openDraft(projectId)}
                onOpenPublic={(slug) => void openPublicSlug(slug)}
                showGoldenFixture={debugControlsAvailable}
                onOpenGoldenFixture={() => {
                  const runtime = runtimeRef.current;
                  if (!runtime) return;
                  const restoreHiddenPanel = !state.debugVisible;
                  if (restoreHiddenPanel) runtime.setDebugVisible(true);
                  runtime.loadDebugFixture('phase9_golden');
                  if (restoreHiddenPanel) runtime.setDebugVisible(false);
                }}
              />
            )}
          </section>
        )}

        {draftPendingDeletion && (
          <Dialog
            id="delete-draft"
            role="alertdialog"
            title="Delete draft?"
            description={`“${draftPendingDeletion.title}” will be permanently deleted from this device.`}
            closeLabel="Cancel"
            onClose={() => setConfirmDeleteProjectId(null)}
          >
            <div>
              <Button type="button" onClick={() => setConfirmDeleteProjectId(null)}>
                Cancel
              </Button>
              <Button
                variant="danger"
                type="button"
                onClick={() => {
                  void runtimeRef.current?.deleteDraft(draftPendingDeletion.projectId);
                  void draftPreviewRepository.delete(draftPendingDeletion.projectId);
                  setDraftPreviewUrls((current) => {
                    const url = current[draftPendingDeletion.projectId];
                    if (url) URL.revokeObjectURL(url);
                    const next = { ...current };
                    delete next[draftPendingDeletion.projectId];
                    draftPreviewUrlsRef.current = next;
                    return next;
                  });
                  setConfirmDeleteProjectId(null);
                }}
              >
                Delete Draft
              </Button>
            </div>
          </Dialog>
        )}

        {inEditor && !visitor && (
          <EditorShell
            state={state}
            runtime={runtimeRef.current}
            coordinateRecoveryEpoch={coordinateRecoveryEpoch}
          />
        )}

        {confirmExit && inEditor && !visitor && (
          <Dialog
            id="exit-confirm"
            title="Exit without Saving?"
            description="All unsaved progress will be lost"
            closeLabel="Keep editing"
            onClose={() => {
              setConfirmExit(false);
              setExitDestination('main_menu');
            }}
          >
            <div>
              <Button
                variant="primary"
                type="button"
                onClick={() => {
                  void saveCurrentDraft().then((saved) => {
                    if (!saved) {
                      projectNameRef.current?.focus();
                      return;
                    }
                    continueToExitDestination();
                  });
                }}
              >
                {exitDestination === 'main_menu' ? 'Save Draft & Exit' : 'Save Draft & Continue'}
              </Button>
              <Button
                variant="danger"
                type="button"
                onClick={() => {
                  continueToExitDestination();
                }}
              >
                Exit without Saving
              </Button>
            </div>
          </Dialog>
        )}

        {confirmPublishSave && inEditor && !visitor && (
          <Dialog
            id="publish-save"
            title={UI_COPY.publish.saveGateTitle}
            description={UI_COPY.publish.saveGateDescription}
            closeLabel={UI_COPY.publish.keepEditing}
            onClose={() => setConfirmPublishSave(false)}
          >
            <div>
              <Button
                variant="primary"
                type="button"
                onClick={() => {
                  void saveCurrentDraft().then((saved) => {
                    if (!saved) {
                      projectNameRef.current?.focus();
                      return;
                    }
                    setConfirmPublishSave(false);
                    openPublishPanel();
                  });
                }}
              >
                {UI_COPY.publish.saveAndContinue}
              </Button>
              <Button type="button" onClick={() => setConfirmPublishSave(false)}>
                {UI_COPY.publish.keepEditing}
              </Button>
            </div>
          </Dialog>
        )}

        {recordRideCamera && (
          <Dialog
            id="record-ride-choice"
            title="Record Ride"
            description="Would you like to include your Reaction Cam in this recording?"
            closeLabel="Cancel recording"
            onClose={() => setRecordRideCamera(null)}
          >
            <div class="record-ride-choice-actions" data-testid="record-ride-choice">
              <Button variant="primary" type="button" onClick={beginRecordRideWithoutReaction}>
                Without Reaction Cam
              </Button>
              <Button
                icon={Sparkles}
                type="button"
                disabled={!reactionCamAvailable}
                title={
                  reactionCamAvailable
                    ? undefined
                    : (state.reaction.eligibility.reason ?? 'Reaction Cam is unavailable.')
                }
                onClick={beginRecordRideWithReaction}
              >
                With Reaction Cam
              </Button>
              {!reactionCamAvailable && (
                <small class="record-ride-reaction-unavailable" role="status">
                  {state.reaction.eligibility.reason}
                </small>
              )}
            </div>
          </Dialog>
        )}

        {inEditor && visitor && (
          <VisitorLaunchPanel
            kind={visitor.kind}
            title={visitor.record.title}
            creatorName={
              visitor.kind === 'public'
                ? visitor.record.creator.displayName
                : visitor.record.project.metadata.creator.displayName
            }
            description={visitor.record.project.metadata.description}
            allowRemix={visitor.record.allowRemix}
            snapshot={state}
            busy={publishBusy}
            message={launchMessage}
            socialCard={visitor.kind === 'public' ? activePublicCard : null}
            onRide={() => runtimeRef.current?.startTestRide('first-person')}
            onRecord={() => openRecordRideChoice('first-person')}
            onRemix={remixVisitorCoaster}
            onBoost={() => {
              if (activePublicCard) void mutateSocial(activePublicCard, 'boost');
            }}
            onFavorite={() => {
              if (activePublicCard) void mutateSocial(activePublicCard, 'favorite');
            }}
            onCopyLink={() => {
              if (activePublicCard) void copyReferralLink(activePublicCard);
            }}
            onHelp={() => setHelpOpen(true)}
            onOnboardingSeen={(kind) => runtimeRef.current?.markRiderOnboardingSeen(kind)}
            onBackToWorld={() => {
              returnVisitorToMainMenu();
            }}
            onWorldHub={() => {
              if (!worldLaunchSession.openContentHub())
                setLaunchMessage('The Buncha Games World Hub is unavailable in this runtime.');
            }}
            onCreator={() => {
              if (
                visitor.kind === 'public' &&
                !worldLaunchSession.openContentHub({
                  creatorPublicId: visitor.record.creator.publicCreatorId,
                })
              )
                setLaunchMessage('Creator discovery is unavailable in this runtime.');
            }}
            worldHubAvailable={worldHubAvailable && visitor.kind !== 'private'}
          />
        )}

        {!confirmExit && !helpOpen && (
          <OnboardingCoachmark state={state} runtime={runtimeRef.current} />
        )}

        {state.onboarding.creator.finishOfferVisible && (
          <Dialog
            id="finish-lesson-offer"
            title="Ready for another challenge?"
            description="Practice bringing a half-built coaster safely back to its station."
            onClose={() => runtimeRef.current?.dismissFinishLessonOffer()}
          >
            <div class="finish-lesson-offer-actions">
              <Button
                variant="primary"
                type="button"
                onClick={() => runtimeRef.current?.startFinishLesson()}
              >
                Try Completing a Half-Built Coaster
              </Button>
              <Button
                type="button"
                onClick={() => {
                  runtimeRef.current?.dismissFinishLessonOffer();
                  runtimeRef.current?.returnToEditor();
                }}
              >
                Keep Building This Coaster
              </Button>
            </div>
          </Dialog>
        )}

        <HelpCenter
          open={helpOpen}
          state={state}
          runtime={runtimeRef.current}
          onClose={() => setHelpOpen(false)}
          onStartCreatorGuide={() => requestExitDestination('creator_guide')}
          onStartFinishChallenge={() => requestExitDestination('finish_challenge')}
          creatorGuideAvailable={!compactViewport}
        />

        {inEditor && !visitor && (
          <PublishPanel
            open={publishOpen}
            sessionId={publishSessionId}
            snapshot={state}
            project={publishProject}
            cloudAvailable={
              viewer.kind === 'authenticated' &&
              platform.getCapability('cloud_drafts').availability === 'available'
            }
            cloudVersion={activeCloudRecord?.version ?? null}
            status={activeStatus as PublishPanelStatus}
            statusUpdatedAt={publishStatus?.updatedAt ?? activeCloudRecord?.updatedAt ?? null}
            busy={publishBusy}
            submitting={publishSubmitting}
            message={publishMessage}
            conflictVersion={cloudConflict?.currentVersion ?? null}
            heroPreviewUrl={heroPreviewUrl}
            heroCaptureState={heroCaptureState}
            catalogHoverPreviewUrl={catalogHoverPreviewUrl}
            catalogHoverState={catalogHoverState}
            catalogHoverProgress={catalogHoverProgress}
            catalogHoverArtifact={catalogHoverArtifact}
            publicUrl={activePublicUrl}
            sourceAttribution={
              activeRemixSource
                ? `${activeRemixSource.title} by ${activeRemixSource.creator.displayName}`
                : null
            }
            onClose={() => {
              clearPublishContext({ preserveCatalogHover: true });
            }}
            onAutoFrame={() => runtimeRef.current?.frameHeroPreview()}
            onCaptureHero={captureHero}
            onSubmit={submitForReview}
            onRetryCatalogHover={() => ensureCatalogHoverVideo().then(() => undefined)}
            onLoadCloudConflict={loadCloudConflict}
            onKeepLocalConflict={keepLocalConflict}
            onRefreshStatus={() =>
              activeCloudRecord
                ? refreshPublishStatus(activeCloudRecord.projectId, true).then(() => undefined)
                : Promise.resolve()
            }
          />
        )}

        {inEditor && !visitor && (
          <ShareDraftDialog
            open={shareDraftOpen}
            title={publishProject?.metadata.title ?? state.fixtureTitle}
            cloudAvailable={shareDraftAvailable}
            busy={publishBusy || shareRidePreparing}
            shareUrl={activeShareUrl}
            shareCreatedAt={readOnlyShare?.createdAt ?? null}
            stale={shareDraftStale || Boolean(readOnlyShare && hasPendingNameChange)}
            message={publishMessage}
            onClose={() => {
              setShareDraftOpen(false);
              setPublishMessage(null);
            }}
            onCreate={createReadOnlyShare}
            onRevoke={revokeReadOnlyShare}
          />
        )}

        {publishOpen &&
          state.debugVisible &&
          cloudRecord &&
          ownerProjectStatusLabel(cloudRecord.state) === 'pending_review' &&
          isLocalPhase4QaPlatformAdapter(platform) && (
            <button
              type="button"
              class="qa-moderation-action"
              onClick={() => void approveLocalQaRevision()}
            >
              Approve local QA revision
            </button>
          )}

        {launchMessage && !visitor && state.mode === 'menu' && (
          <div class="launch-message" role="alert">
            <strong>Coaster unavailable</strong>
            <span>{launchMessage}</span>
            <button
              type="button"
              onClick={() => {
                setLaunchMessage(null);
                history.replaceState(null, '', editorResetPath);
              }}
            >
              Browse coasters
            </button>
          </div>
        )}

        {inRide && (
          <>
            {reactionUi && <reactionUi.LiveReactionPane state={state.reaction} />}
            <div class="ride-telemetry" aria-label="Live ride telemetry">
              <div>
                <span>Front car speed</span>
                <strong data-testid="speed-value">{mph(state.telemetry.speedMph)}</strong>
              </div>
              <div>
                <span>Front car vertical G</span>
                <strong>{force(state.telemetry.verticalG)}</strong>
              </div>
              <div>
                <span>Front car lateral G</span>
                <strong>{force(state.telemetry.lateralG)}</strong>
              </div>
              <div>
                <span>Track</span>
                <strong>{state.ride.progressPercent.toFixed(0)}%</strong>
              </div>
            </div>
            <div class={`section-status section-${state.telemetry.activeSectionType ?? 'normal'}`}>
              <span>Track section</span>
              <strong data-testid="active-section">{state.telemetry.activeSectionLabel}</strong>
            </div>
            {waitingForDispatch && (
              <div
                class="dispatch-status"
                role="status"
                aria-live="polite"
                data-testid="dispatch-status"
              >
                <span>Station</span>
                <strong>
                  {state.ride.dispatchSecondsRemaining > 0
                    ? `Departing in ${Math.max(1, Math.ceil(state.ride.dispatchSecondsRemaining))}`
                    : 'Dispatching train...'}
                </strong>
              </div>
            )}
            {state.mode === 'replay' ? (
              <div class="ride-controls replay-controls" data-testid="replay-controls">
                <div>
                  <strong>Cinematic Replay</strong>
                  <span>{state.replay.progressPercent.toFixed(0)}%</span>
                </div>
                <button type="button" onClick={() => runtimeRef.current?.toggleReplayPaused()}>
                  {state.replay.paused ? 'Play' : 'Pause'}
                </button>
                <TestSpeedControl value={state.settings.testSpeed} runtime={runtimeRef.current} />
                <IconButton
                  class="ride-sound-toggle"
                  label={state.settings.muted ? 'Turn Sound On' : 'Turn Sound Off'}
                  icon={state.settings.muted ? VolumeX : Volume2}
                  size="compact"
                  type="button"
                  data-audio-cue="none"
                  aria-pressed={state.settings.muted}
                  onClick={() => runtimeRef.current?.setMuted(!state.settings.muted)}
                />
                <button
                  class="secondary-action"
                  type="button"
                  onClick={() => runtimeRef.current?.returnToEditor()}
                >
                  {visitor ? 'Return to coaster' : 'Back to Build'}
                </button>
              </div>
            ) : state.ride.phase === 'running' ? (
              <div class="ride-controls">
                <div class="camera-toggle" role="group" aria-label="Ride camera">
                  <button
                    type="button"
                    aria-pressed={camera === 'first-person'}
                    onClick={() => runtimeRef.current?.setRideCamera('first-person')}
                  >
                    First person
                  </button>
                  <button
                    type="button"
                    aria-pressed={camera === 'third-person'}
                    onClick={() => runtimeRef.current?.setRideCamera('third-person')}
                  >
                    Third person
                  </button>
                </div>
                <TestSpeedControl
                  value={state.settings.testSpeed}
                  runtime={runtimeRef.current}
                  locked={state.ride.intent === 'record'}
                />
                <IconButton
                  class="ride-sound-toggle"
                  label={state.settings.muted ? 'Turn Sound On' : 'Turn Sound Off'}
                  icon={state.settings.muted ? VolumeX : Volume2}
                  size="compact"
                  type="button"
                  data-audio-cue="none"
                  aria-pressed={state.settings.muted}
                  onClick={() => runtimeRef.current?.setMuted(!state.settings.muted)}
                />
                <button
                  class="secondary-action"
                  type="button"
                  onClick={() => runtimeRef.current?.returnToEditor()}
                >
                  {visitor ? 'Return to coaster' : 'Back to Build'}
                </button>
                {state.reaction.phase === 'recording' && (
                  <button
                    class="danger-action"
                    type="button"
                    onClick={() => void runtimeRef.current?.stopAndDiscardReaction()}
                  >
                    Stop &amp; Discard Reaction
                  </button>
                )}
              </div>
            ) : (
              <section
                class="ride-result"
                aria-labelledby="ride-result-title"
                data-testid="ride-result"
              >
                {!visitor && (
                  <span class="eyebrow">
                    {state.ride.intent === 'record' ? 'Recorded Ride' : 'Test Result'}
                  </span>
                )}
                <h2 id="ride-result-title">
                  {state.ride.phase === 'completed'
                    ? visitor
                      ? 'Ride Complete!'
                      : 'Track Complete!'
                    : state.ride.phase === 'arrival_failed'
                      ? 'Station Arrival Failed'
                      : 'Ride Stalled'}
                </h2>
                {(!visitor || state.ride.phase !== 'completed') && (
                  <p>
                    {state.ride.phase === 'completed'
                      ? 'Your train finished the circuit and reached the station.'
                      : state.ride.phase === 'arrival_failed'
                        ? 'The train passed through the station too fast. Strengthen the final brakes.'
                        : 'Lower the next hill or add more speed, then try again.'}
                  </p>
                )}
                <div
                  class={`intensity-callout intensity-${state.ride.intensity.level}`}
                  data-testid="intensity-result"
                >
                  <strong>
                    {state.ride.intensity.score}/100 · {state.ride.intensity.label}
                  </strong>
                  <small>
                    Strongest moment around {state.ride.intensity.progressPercent.toFixed(0)}%.
                  </small>
                </div>
                <dl>
                  <div>
                    <dt>Top speed</dt>
                    <dd data-testid="result-top-speed">{mph(state.telemetry.peakSpeedMph)}</dd>
                  </div>
                  <div>
                    <dt>Positive G</dt>
                    <dd data-testid="result-positive-g">{force(state.ride.peakVerticalG)}</dd>
                    <small>Near {state.ride.peakVerticalProgressPercent.toFixed(0)}%</small>
                  </div>
                  <div>
                    <dt>Lowest G</dt>
                    <dd data-testid="result-negative-g">{force(state.ride.minVerticalG)}</dd>
                    <small>Near {state.ride.minVerticalProgressPercent.toFixed(0)}%</small>
                  </div>
                  <div>
                    <dt>Peak lateral G</dt>
                    <dd data-testid="result-lateral-g">{force(state.ride.peakLateralG)}</dd>
                    <small>Near {state.ride.peakLateralProgressPercent.toFixed(0)}%</small>
                  </div>
                  <div>
                    <dt>Track reached</dt>
                    <dd>{state.ride.progressPercent.toFixed(0)}%</dd>
                  </div>
                  <div>
                    <dt>Ride length</dt>
                    <dd data-testid="result-ride-length">
                      {state.ride.elapsedSeconds.toFixed(1)} s
                    </dd>
                  </div>
                </dl>
                <div
                  class={`ride-result-actions ${
                    visitor
                      ? `ride-result-visitor ${visitor.kind !== 'public' ? 'ride-result-visitor-private' : ''}`
                      : 'ride-result-owner'
                  }`}
                  data-onboarding-target={
                    state.onboarding.creator.step === 'unlocks' ? 'post-test-actions' : undefined
                  }
                >
                  <Button
                    class="ride-result-primary ride-result-ride-again"
                    variant="primary"
                    type="button"
                    disabled={creatorGuideCompletionPending}
                    onClick={() => {
                      if (state.ride.intent === 'record') openRecordRideChoice(camera);
                      else runtimeRef.current?.restartRide(camera);
                    }}
                  >
                    {state.ride.intent === 'record'
                      ? 'Record Again'
                      : visitor
                        ? 'Ride Again'
                        : 'Test Again'}
                  </Button>
                  {!visitor && (
                    <Button
                      class="ride-result-secondary ride-result-back"
                      variant="secondary"
                      type="button"
                      disabled={creatorGuideCompletionPending}
                      onClick={() => runtimeRef.current?.returnToEditor()}
                    >
                      Back to Build
                    </Button>
                  )}
                  {state.ride.phase === 'completed' && state.ride.intent === 'test' && (
                    <Button
                      class="ride-result-secondary ride-result-record"
                      variant="secondary"
                      type="button"
                      disabled={creatorGuideCompletionPending || !state.recording.allowed}
                      title={state.recording.disabledReason ?? undefined}
                      onClick={() => openRecordRideChoice(camera)}
                    >
                      Record Ride
                    </Button>
                  )}
                  {visitor && (
                    <Button
                      class="ride-result-secondary ride-result-back"
                      variant="secondary"
                      type="button"
                      onClick={() =>
                        visitor.kind !== 'public'
                          ? returnVisitorToMainMenu()
                          : runtimeRef.current?.returnToEditor()
                      }
                    >
                      {visitor.kind !== 'public'
                        ? 'Back to Buncha Coasters Main Menu'
                        : 'Return to coaster'}
                    </Button>
                  )}
                  {state.ride.phase === 'completed' &&
                    state.ride.intent === 'record' &&
                    !state.media.requiresNewRide && (
                      <Button
                        class="ride-result-secondary ride-result-media"
                        variant="secondary"
                        type="button"
                        disabled={creatorGuideCompletionPending}
                        onClick={() => void runtimeRef.current?.openMediaStudio()}
                      >
                        {UI_COPY.media.create}
                      </Button>
                    )}
                  {state.ride.phase === 'completed' && !visitor && (
                    <Button
                      class="ride-result-secondary ride-result-share-draft"
                      variant="secondary"
                      type="button"
                      disabled={creatorGuideCompletionPending || !shareRideAllowed}
                      title={
                        !state.recording.allowed
                          ? POST_TEST_ACTIONS_REASON
                          : !shareDraftAvailable
                            ? shareDraftCapability.reason
                            : undefined
                      }
                      onClick={() => openOwnerFlowFromRide('share_draft')}
                    >
                      Share Ride
                    </Button>
                  )}
                  {state.ride.phase === 'completed' &&
                    state.ride.intent === 'record' &&
                    state.media.requiresNewRide && (
                      <p class="ride-media-deleted" role="status">
                        Ride files deleted. Complete another ride to create new ones.
                      </p>
                    )}
                  {state.ride.phase === 'completed' && !visitor && (
                    <Button
                      class="ride-result-secondary ride-result-publish"
                      variant="secondary"
                      type="button"
                      disabled={creatorGuideCompletionPending}
                      onClick={() => openOwnerFlowFromRide('publish')}
                    >
                      Publish to Buncha Games
                    </Button>
                  )}
                </div>
              </section>
            )}
          </>
        )}

        {state.debugVisible && (
          <aside class="debug-panel" data-testid="debug-panel">
            <span class="eyebrow">Debug Stats</span>
            <dl>
              <div>
                <dt>Quality</dt>
                <dd>{state.diagnostics.qualityTier}</dd>
              </div>
              <div>
                <dt>Samples</dt>
                <dd>{state.diagnostics.sampleCount}</dd>
              </div>
              <div>
                <dt>Changed spans</dt>
                <dd>{state.diagnostics.changedSpans}</dd>
              </div>
              <div>
                <dt>Rebuild</dt>
                <dd>{state.diagnostics.rebuildMs.toFixed(1)} ms</dd>
              </div>
              <div>
                <dt>Track length</dt>
                <dd>{state.diagnostics.trackLengthM.toFixed(1)} m</dd>
              </div>
              <div>
                <dt>Geometry rev</dt>
                <dd>{state.diagnostics.geometryRevision}</dd>
              </div>
              <div>
                <dt>Frame p50 / p95</dt>
                <dd>
                  {state.diagnostics.frameMsP50.toFixed(1)} /{' '}
                  {state.diagnostics.frameMsP95.toFixed(1)} ms
                </dd>
              </div>
              <div>
                <dt>Input p50 / p95</dt>
                <dd>
                  {state.diagnostics.inputMsP50.toFixed(1)} /{' '}
                  {state.diagnostics.inputMsP95.toFixed(1)} ms
                </dd>
              </div>
              <div>
                <dt>Renderer</dt>
                <dd>
                  {state.diagnostics.rendererGeometries} geo / {state.diagnostics.rendererTextures}{' '}
                  tex
                </dd>
              </div>
              <div>
                <dt>Draw calls / triangles</dt>
                <dd>
                  {state.diagnostics.rendererDrawCalls} /{' '}
                  {state.diagnostics.rendererTriangles.toLocaleString()}
                </dd>
              </div>
              <div>
                <dt>Visible renderables</dt>
                <dd>{state.diagnostics.renderableCounts}</dd>
              </div>
              <div>
                <dt>Renderable roots</dt>
                <dd>{state.diagnostics.renderableRoots}</dd>
              </div>
              <div>
                <dt>Production batches</dt>
                <dd>{state.diagnostics.renderableBatches}</dd>
              </div>
              <div>
                <dt>Train renderables</dt>
                <dd>{state.diagnostics.trainRenderableTypes}</dd>
              </div>
              <div>
                <dt>Ride cycles / trace</dt>
                <dd>
                  {state.diagnostics.rideCycles} / {state.diagnostics.retainedTraceSamples}
                </dd>
              </div>
              <div>
                <dt>Media trace / URLs</dt>
                <dd>
                  {state.diagnostics.retainedMediaSamples} / {state.diagnostics.mediaArtifactUrls}
                  {state.diagnostics.mediaExportActive ? ' · exporting' : ''}
                </dd>
              </div>
              <div>
                <dt>Reaction tracks / recorder</dt>
                <dd>
                  {state.diagnostics.reactionActiveTracks} /{' '}
                  {state.diagnostics.reactionRecorderActive ? 'active' : 'off'}
                </dd>
              </div>
              <div>
                <dt>Reaction retained</dt>
                <dd>{Math.ceil(state.diagnostics.reactionRetainedBytes / 1024)} KiB</dd>
              </div>
              <div>
                <dt>Validation active / queued</dt>
                <dd>
                  {state.diagnostics.validationActive} / {state.diagnostics.validationQueued}
                </dd>
              </div>
              <div>
                <dt>Audio voices / loops / listeners</dt>
                <dd>
                  {state.diagnostics.audioActiveVoices} / {state.diagnostics.audioActiveLoops} /{' '}
                  {state.diagnostics.listenerCount}
                </dd>
              </div>
              <div>
                <dt>Rail seam</dt>
                <dd data-testid="rail-seam-gap">
                  {state.diagnostics.maxRailSeamGapM.toExponential(1)} m
                </dd>
              </div>
              <div>
                <dt>Seam angle</dt>
                <dd>{state.diagnostics.maxRailSeamAngleDegrees.toFixed(3)}°</dd>
              </div>
              <div>
                <dt>Max tie gap</dt>
                <dd>{state.diagnostics.maxTieSpacingM.toFixed(3)} m</dd>
              </div>
              <div>
                <dt>Local draft</dt>
                <dd>{state.persistenceAvailable ? 'available' : 'unavailable'}</dd>
              </div>
              <div>
                <dt>Cloud writes</dt>
                <dd>
                  {localQaContext
                    ? 'local QA simulation'
                    : platform.getCapability('cloud_drafts').availability === 'available'
                      ? 'available'
                      : 'unavailable (fail-soft)'}
                </dd>
              </div>
            </dl>
            <div class="debug-fixtures" role="group" aria-label="Debug coaster fixture">
              <button
                type="button"
                data-testid="template-card-systems"
                onClick={() => runtimeRef.current?.loadDebugFixture('systems')}
              >
                Systems Circuit
              </button>
              <button type="button" onClick={() => runtimeRef.current?.loadDebugFixture('stall')}>
                Stall Test
              </button>
              <button type="button" onClick={() => runtimeRef.current?.loadDebugFixture('extreme')}>
                Extreme Warning Test
              </button>
              <button
                type="button"
                data-testid="template-card-clearance"
                onClick={() => runtimeRef.current?.loadDebugFixture('clearance')}
              >
                Clearance Conflict
              </button>
              <button
                type="button"
                data-testid="template-card-loopworks"
                onClick={() => runtimeRef.current?.loadDebugFixture('loopworks')}
              >
                The Loopworks
              </button>
            </div>
            <div
              class="debug-fixtures debug-review-controls"
              aria-label="Phase 9 visual review controls"
            >
              <label>
                <span>Review theme</span>
                <select
                  defaultValue="park_model"
                  onChange={(event) =>
                    runtimeRef.current?.setPhase9ReviewTheme(
                      event.currentTarget.value as 'park_model' | 'toyworks' | 'afterglow',
                    )
                  }
                >
                  <option value="park_model">Park Model</option>
                  <option value="toyworks">Toyworks</option>
                  <option value="afterglow">Afterglow</option>
                </select>
              </label>
              <label>
                <span>Review camera</span>
                <select
                  defaultValue="editor"
                  onChange={(event) =>
                    runtimeRef.current?.setPhase9ReviewCamera(
                      event.currentTarget.value as Phase9ReviewCameraId,
                    )
                  }
                >
                  <option value="editor">Editor overview</option>
                  <option value="station">Station close-up</option>
                  <option value="first_person">First-person pose</option>
                  <option value="follow">Follow pose</option>
                  <option value="head_on">Head-on train</option>
                  <option value="banked_side">Banked side</option>
                  <option value="inversion_underside">Inversion underside</option>
                  <option value="hero">Hero camera</option>
                  <option value="orbit_quarter">Orbit quarter-turn</option>
                </select>
              </label>
              <label>
                <span>Creator-color stress palette</span>
                <select
                  defaultValue="launch_yellow"
                  onChange={(event) =>
                    runtimeRef.current?.setPhase9ReviewPalette(
                      event.currentTarget.value as Phase9ReviewPaletteId,
                    )
                  }
                >
                  <option value="launch_yellow">Open Arc launch colors</option>
                  <option value="near_black">Near black</option>
                  <option value="near_white">Near white</option>
                  <option value="high_chroma">High chroma</option>
                  <option value="low_contrast">Low contrast</option>
                </select>
              </label>
            </div>
            {(state.mode === 'ride-first-person' || state.mode === 'ride-third-person') && (
              <button
                type="button"
                data-testid="complete-ride-diagnostics"
                onClick={() => runtimeRef.current?.completeRideForDiagnostics()}
              >
                Skip to ride result (QA)
              </button>
            )}
            <button
              type="button"
              onClick={() =>
                void runtimeRef.current
                  ?.captureEvidence()
                  .then((blob) => downloadBlob(blob, 'buncha-coasters-phase3-evidence.png'))
              }
            >
              Capture frame
            </button>
          </aside>
        )}

        {state.media.open && runtimeRef.current && (
          <MediaStudio
            state={state.media}
            monetization={state.monetization}
            runtime={runtimeRef.current}
            title={state.fixtureTitle}
            ReactionOptions={reactionUi?.MediaReactionOptions}
          />
        )}

        {state.reaction.setupOpen && runtimeRef.current && reactionUi && (
          <reactionUi.ReactionCamSetup state={state.reaction} runtime={runtimeRef.current} />
        )}

        {(!state.ready || holdLoadingMark || worldLaunchPending) && (
          <div class="app-loading-screen" data-testid="app-loading-screen">
            <LoadingMark
              label={
                launchMessage?.startsWith('Loading')
                  ? launchMessage.replace(/…$/u, '')
                  : 'Loading Buncha Coasters'
              }
            />
          </div>
        )}

        {!(state.mode === 'menu' && !visitor) && (
          <div class={`status-toast status-${statusTone}`} role="status">
            {state.status}
          </div>
        )}
      </section>
    </main>
  );
}
